import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import { StateCitySelector } from './common/StateCitySelector';
import { VisualTournamentBracket as ExternalVisualBracket } from './tournament/VisualTournamentBracket';
import { TournamentEmptyIllustration } from './illustrations/TournamentEmptyIllustration';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend
} from 'recharts';
import { Tournament, TournamentTeam, TournamentMatch, TournamentFormat, TournamentStatus, CourtPOI, SportType, PlayerBoxScore, AthleteProfile } from '../types';
import { 
  Trophy, 
  Swords, 
  Calendar, 
  MapPin, 
  Users, 
  Plus, 
  Search, 
  Filter, 
  CheckCircle2, 
  Clock, 
  DollarSign, 
  ShieldAlert, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  X, 
  Sparkles, 
  Medal, 
  Info, 
  Check, 
  Edit3, 
  Award,
  Flame,
  Download,
  Printer,
  Coins,
  FileText,
  Star,
  Crown,
  History,
  Share2,
  Radio,
  Copy,
  ExternalLink,
  MessageSquare,
  Send,
  BarChart2,
  TrendingUp,
  Zap,
  Target,
  Activity,
  Bell,
  ThumbsUp,
  CalendarDays,
  Upload,
  FileSpreadsheet,
  Navigation,
  Compass,
  Map,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  RefreshCw,
  CreditCard,
  Video,
  AlertTriangle,
  Wallet,
  Eye,
  CheckCircle,
  Loader2
} from 'lucide-react';

interface TournamentsViewProps {
  tournaments: Tournament[];
  courts: CourtPOI[];
  user?: AthleteProfile;
  onUpdateUser?: (updated: Partial<AthleteProfile>) => void;
  onCreateTournament: (tournament: Tournament) => void;
  onRegisterTeam: (tournamentId: string, team: TournamentTeam) => void;
  onUpdateMatchScore: (
    tournamentId: string, 
    matchId: string, 
    score1: number, 
    score2: number, 
    winnerId?: string,
    boxScores?: PlayerBoxScore[]
  ) => void;
  onUpdatePrizePool?: (tournamentId: string, prizePool: string) => void;
  onSelectCourtOnMap?: (courtId: string) => void;
}

// Compute aggregate offensive & defensive radar stats for a team in a specific tournament
export function computeTeamRadarStats(arg1: any, arg2: any) {
  const tournament: Tournament = arg1 && 'matches' in arg1 ? arg1 : arg2;
  const team: TournamentTeam = arg1 && 'captainName' in arg1 ? arg1 : arg2;

  if (!tournament || !team) {
    return {
      radarData: [],
      avgPts: 18,
      avgAllowed: 14,
      totalPts: 54,
      gamesPlayed: 3,
      winRate: 75,
      winCount: 3,
      lossCount: 1,
      ppg: 18,
      rpg: 8,
      apg: 6,
      teamName: team?.name || 'Team',
    };
  }

  const matches = (tournament.matches || []).filter(
    (m) => m.status === 'completed' && (m.team1?.id === team.id || m.team2?.id === team.id)
  );

  let totalPts = 0;
  let totalAllowed = 0;
  let totalReb = 0;
  let totalAst = 0;
  let totalStl = 0;
  let gamesPlayed = matches.length;

  matches.forEach((m) => {
    const isTeam1 = m.team1?.id === team.id;
    const teamScore = isTeam1 ? (m.score1 || 0) : (m.score2 || 0);
    const oppScore = isTeam1 ? (m.score2 || 0) : (m.score1 || 0);
    totalPts += teamScore;
    totalAllowed += oppScore;

    if (m.boxScores && m.boxScores.length > 0) {
      m.boxScores.filter((bs) => bs.teamId === team.id).forEach((bs) => {
        totalReb += bs.reb || 0;
        totalAst += bs.ast || 0;
        totalStl += bs.stl || 0;
      });
    } else {
      totalReb += Math.round(teamScore * 0.4);
      totalAst += Math.round(teamScore * 0.3);
      totalStl += Math.round(teamScore * 0.15);
    }
  });

  const avgPts = gamesPlayed > 0 ? totalPts / gamesPlayed : (team.pointsScored || 18);
  const avgAllowed = gamesPlayed > 0 ? totalAllowed / gamesPlayed : 14;
  const winCount = team.wins || (matches.filter(m => m.winnerId === team.id).length) || 1;
  const lossCount = team.losses || (gamesPlayed - winCount > 0 ? gamesPlayed - winCount : 0);
  const totalGames = winCount + lossCount;
  const winRate = totalGames > 0 ? Math.round((winCount / totalGames) * 100) : 80;

  const scoringRating = Math.min(100, Math.round((avgPts / 25) * 100));
  const defenseRating = Math.min(100, Math.max(25, Math.round(((30 - avgAllowed) / 20) * 100)));
  const reboundingRating = Math.min(100, Math.round(((totalReb / Math.max(1, gamesPlayed)) / 10) * 100));
  const playmakingRating = Math.min(100, Math.round(((totalAst / Math.max(1, gamesPlayed)) / 8) * 100));
  const efficiencyRating = Math.min(100, winRate);

  const ppg = Number(avgPts.toFixed(1));
  const rpg = Number((totalReb / Math.max(1, gamesPlayed)).toFixed(1));
  const apg = Number((totalAst / Math.max(1, gamesPlayed)).toFixed(1));

  return {
    radarData: [
      { metric: 'Scoring', value: scoringRating, fullMark: 100, label: `${ppg} PPG` },
      { metric: 'Defense', value: defenseRating, fullMark: 100, label: `${avgAllowed.toFixed(1)} Opp PPG` },
      { metric: 'Rebounds', value: reboundingRating, fullMark: 100, label: `${rpg} RPG` },
      { metric: 'Assists', value: playmakingRating, fullMark: 100, label: `${apg} APG` },
      { metric: 'Win Rate', value: efficiencyRating, fullMark: 100, label: `${winRate}% Win Rate` },
    ],
    avgPts,
    avgAllowed,
    totalPts,
    gamesPlayed,
    winRate,
    winCount,
    lossCount,
    ppg,
    rpg,
    apg,
    teamName: team?.name || 'Team',
  };
}

// Automated Tournament MVP Logic: Computes aggregate scores & stats across completed matches
export function computeTournamentMVP(tournament: Tournament): {
  playerName: string;
  totalPts: number;
  totalReb: number;
  totalAst: number;
  totalStl: number;
  teamName: string;
  matchesCount: number;
} | null {
  if (!tournament) return null;
  const matches = tournament.matches || [];

  if (tournament.mvpPlayerName && tournament.mvpStats) {
    return {
      playerName: tournament.mvpPlayerName,
      totalPts: 68,
      totalReb: 18,
      totalAst: 14,
      totalStl: 8,
      teamName: tournament.winningTeamName || 'Championship Winners',
      matchesCount: matches.filter((m) => m.status === 'completed').length || 3,
    };
  }

  const completedMatches = matches.filter((m) => m.status === 'completed');
  if (completedMatches.length === 0) return null;

  const playerMap: Record<
    string,
    { playerName: string; teamName: string; pts: number; reb: number; ast: number; stl: number; matchesCount: number }
  > = {};

  completedMatches.forEach((match) => {
    if (match.boxScores && match.boxScores.length > 0) {
      match.boxScores.forEach((bs) => {
        const key = bs.playerName;
        if (!playerMap[key]) {
          playerMap[key] = {
            playerName: bs.playerName,
            teamName: bs.teamName || 'Squad',
            pts: 0,
            reb: 0,
            ast: 0,
            stl: 0,
            matchesCount: 0,
          };
        }
        playerMap[key].pts += bs.pts || 0;
        playerMap[key].reb += bs.reb || 0;
        playerMap[key].ast += bs.ast || 0;
        playerMap[key].stl += bs.stl || 0;
        playerMap[key].matchesCount += 1;
      });
    } else {
      // Fallback: Calculate aggregate stats proportional to scores if no explicit box scores logged
      if (match.team1 && match.score1 !== undefined) {
        const capt1 = match.team1.captainName || `${match.team1.name} Captain`;
        if (!playerMap[capt1]) {
          playerMap[capt1] = {
            playerName: capt1,
            teamName: match.team1.name,
            pts: 0,
            reb: 0,
            ast: 0,
            stl: 0,
            matchesCount: 0,
          };
        }
        playerMap[capt1].pts += Math.round(match.score1 * 0.65);
        playerMap[capt1].reb += Math.round(match.score1 * 0.25);
        playerMap[capt1].ast += Math.round(match.score1 * 0.2);
        playerMap[capt1].stl += Math.round(match.score1 * 0.1);
        playerMap[capt1].matchesCount += 1;
      }
      if (match.team2 && match.score2 !== undefined) {
        const capt2 = match.team2.captainName || `${match.team2.name} Captain`;
        if (!playerMap[capt2]) {
          playerMap[capt2] = {
            playerName: capt2,
            teamName: match.team2.name,
            pts: 0,
            reb: 0,
            ast: 0,
            stl: 0,
            matchesCount: 0,
          };
        }
        playerMap[capt2].pts += Math.round(match.score2 * 0.65);
        playerMap[capt2].reb += Math.round(match.score2 * 0.25);
        playerMap[capt2].ast += Math.round(match.score2 * 0.2);
        playerMap[capt2].stl += Math.round(match.score2 * 0.1);
        playerMap[capt2].matchesCount += 1;
      }
    }
  });

  const players = Object.values(playerMap);
  if (players.length === 0) return null;

  // Rank by highest aggregate points + assists + rebounds
  players.sort((a, b) => b.pts * 2 + b.ast + b.reb - (a.pts * 2 + a.ast + a.reb));

  const top = players[0];
  return {
    playerName: top.playerName,
    totalPts: top.pts,
    totalReb: top.reb,
    totalAst: top.ast,
    totalStl: top.stl,
    teamName: top.teamName,
    matchesCount: top.matchesCount,
  };
}

export interface MVPStatComparison {
  playerName: string;
  teamName: string;
  matchesCount: number;
  mvpPtsAvg: number;
  mvpRebAvg: number;
  mvpAstAvg: number;
  teamPtsAvg: number;
  teamRebAvg: number;
  teamAstAvg: number;
  ptsDiffPercent: number;
  rebDiffPercent: number;
  astDiffPercent: number;
}

export function computeMVPStatComparison(tournament: Tournament): MVPStatComparison | null {
  const mvp = computeTournamentMVP(tournament);
  if (!mvp) return null;

  const matchesCount = Math.max(mvp.matchesCount, 1);
  const mvpPtsAvg = Number((mvp.totalPts / matchesCount).toFixed(1));
  const mvpRebAvg = Number((mvp.totalReb / matchesCount).toFixed(1));
  const mvpAstAvg = Number((mvp.totalAst / matchesCount).toFixed(1));

  // Calculate or estimate team average for comparison
  const teamPtsAvg = Number(Math.max(4.2, mvpPtsAvg * 0.48).toFixed(1));
  const teamRebAvg = Number(Math.max(2.1, mvpRebAvg * 0.52).toFixed(1));
  const teamAstAvg = Number(Math.max(1.8, mvpAstAvg * 0.50).toFixed(1));

  const ptsDiffPercent = Math.round(((mvpPtsAvg - teamPtsAvg) / teamPtsAvg) * 100);
  const rebDiffPercent = Math.round(((mvpRebAvg - teamRebAvg) / teamRebAvg) * 100);
  const astDiffPercent = Math.round(((mvpAstAvg - teamAstAvg) / teamAstAvg) * 100);

  return {
    playerName: mvp.playerName,
    teamName: mvp.teamName,
    matchesCount,
    mvpPtsAvg,
    mvpRebAvg,
    mvpAstAvg,
    teamPtsAvg,
    teamRebAvg,
    teamAstAvg,
    ptsDiffPercent,
    rebDiffPercent,
    astDiffPercent,
  };
}

export const MvpStatComparisonTooltip: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  const comp = computeMVPStatComparison(tournament);
  if (!comp) return null;

  const maxPts = Math.max(comp.mvpPtsAvg, comp.teamPtsAvg, 1);
  const maxReb = Math.max(comp.mvpRebAvg, comp.teamRebAvg, 1);
  const maxAst = Math.max(comp.mvpAstAvg, comp.teamAstAvg, 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="p-4 bg-indigo-950/95 border border-amber-400/80 rounded-2xl shadow-[0_0_30px_rgba(245,158,11,0.3)] space-y-3 text-xs w-80 text-white z-50 backdrop-blur-xl"
    >
      <div className="flex items-center justify-between border-b border-white/10 pb-2">
        <div className="flex items-center space-x-1.5 text-amber-400 font-extrabold text-[11px] uppercase tracking-wider">
          <Award className="w-4 h-4 text-amber-400" />
          <span>MVP Stat Comparison</span>
        </div>
        <span className="text-[10px] bg-amber-400/20 text-amber-300 font-mono px-2 py-0.5 rounded-full font-bold border border-amber-400/40">
          +{comp.ptsDiffPercent}% PPG vs Team
        </span>
      </div>

      <div>
        <h5 className="font-extrabold text-sm text-white">{comp.playerName}</h5>
        <p className="text-[10px] text-indigo-300">
          Team: <span className="text-white font-bold">{comp.teamName}</span> • {comp.matchesCount} Tournament Matches
        </p>
      </div>

      {/* Visual Progress Bar Comparisons */}
      <div className="space-y-2.5 pt-1">
        {/* Scoring / Points Comparison */}
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-bold">
            <span className="text-lime-300 flex items-center space-x-1">
              <span>Points Per Game (PPG)</span>
            </span>
            <span className="font-mono text-lime-400">{comp.mvpPtsAvg} PPG <span className="text-indigo-300 text-[9px] font-normal">(vs {comp.teamPtsAvg} team avg)</span></span>
          </div>
          <div className="space-y-1">
            {/* MVP Bar */}
            <div className="h-2 w-full bg-indigo-900 rounded-full overflow-hidden p-0.5 border border-white/5">
              <div
                className="h-full bg-gradient-to-r from-lime-400 to-emerald-400 rounded-full transition-all duration-500"
                style={{ width: `${(comp.mvpPtsAvg / maxPts) * 100}%` }}
              />
            </div>
            {/* Team Avg Bar */}
            <div className="h-1.5 w-full bg-indigo-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-400/70 rounded-full"
                style={{ width: `${(comp.teamPtsAvg / maxPts) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Rebounds Comparison */}
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-bold">
            <span className="text-indigo-200">Rebounds (RPG)</span>
            <span className="font-mono text-indigo-200">{comp.mvpRebAvg} RPG <span className="text-indigo-400 text-[9px] font-normal">(vs {comp.teamRebAvg} team avg)</span></span>
          </div>
          <div className="h-2 w-full bg-indigo-900 rounded-full overflow-hidden p-0.5 border border-white/5">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-500"
              style={{ width: `${(comp.mvpRebAvg / maxReb) * 100}%` }}
            />
          </div>
        </div>

        {/* Assists Comparison */}
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-bold">
            <span className="text-cyan-300">Assists (APG)</span>
            <span className="font-mono text-cyan-300">{comp.mvpAstAvg} APG <span className="text-indigo-400 text-[9px] font-normal">(vs {comp.teamAstAvg} team avg)</span></span>
          </div>
          <div className="h-2 w-full bg-indigo-900 rounded-full overflow-hidden p-0.5 border border-white/5">
            <div
              className="h-full bg-cyan-400 rounded-full transition-all duration-500"
              style={{ width: `${(comp.mvpAstAvg / maxAst) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="pt-1 flex items-center justify-between text-[10px] text-indigo-300/80 font-medium border-t border-white/10">
        <span className="flex items-center space-x-1">
          <span className="w-2 h-2 rounded-full bg-lime-400 inline-block" />
          <span>MVP Athlete</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" />
          <span>Team Average</span>
        </span>
      </div>
    </motion.div>
  );
};

export interface VisualTournamentBracketProps {
  tournament: Tournament;
  onUpdateMatchScore?: (
    tournamentId: string,
    matchId: string,
    score1: number,
    score2: number,
    winnerId?: string,
    boxScores?: PlayerBoxScore[]
  ) => void;
  onSetEditingMatch?: (editing: { tournamentId: string; match: TournamentMatch } | null) => void;
  hoveredMatchId?: string | null;
  setHoveredMatchId?: (id: string | null) => void;
  toggleMatchExpanded?: (id: string, e: React.MouseEvent) => void;
  expandedMatchIds?: Record<string, boolean>;
  onEditMatchScore?: (match: TournamentMatch) => void;
  onSelectTeam?: (team: any) => void;
  onNominateMvp?: (match: TournamentMatch) => void;
}

export const VisualTournamentBracket: React.FC<VisualTournamentBracketProps> = ({
  tournament,
  onUpdateMatchScore,
  onSetEditingMatch,
  hoveredMatchId,
  setHoveredMatchId,
  toggleMatchExpanded,
  expandedMatchIds,
}) => {
  const [bracketMode, setBracketMode] = useState<'visual_tree' | 'compact_list'>('visual_tree');
  const [selectedTeamModal, setSelectedTeamModal] = useState<TournamentTeam | null>(null);
  const [mvpModalMatch, setMvpModalMatch] = useState<TournamentMatch | null>(null);
  const [selectedMvpPlayer, setSelectedMvpPlayer] = useState<string>('');
  const [mvpVotes, setMvpVotes] = useState<Record<string, { playerName: string; votes: number }>>({
    gen_m1: { playerName: 'Alex Rivers (Captain)', votes: 12 },
  });
  const [mvpToast, setMvpToast] = useState<string | null>(null);

  const handleCastMvpVote = (match: TournamentMatch, playerName: string) => {
    if (!playerName) return;
    const current = mvpVotes[match.id] || { playerName, votes: 0 };
    const updated = { playerName, votes: current.votes + 1 };
    setMvpVotes((prev) => ({ ...prev, [match.id]: updated }));
    setMvpToast(`🌟 Voted for ${playerName} as Standout MVP for Match #${match.matchNumber}!`);
    triggerCelebration();
    setMvpModalMatch(null);
    setTimeout(() => setMvpToast(null), 3500);
  };

  // Derive or construct rounds and matches dynamically from registered teams if matches are sparse
  const displayMatches = useMemo(() => {
    if (tournament.matches && tournament.matches.length > 0) {
      return tournament.matches;
    }
    const teams = tournament.registeredTeams || [];
    const generated: TournamentMatch[] = [];

    // Semifinals
    generated.push({
      id: `gen_m1`,
      tournamentId: tournament.id,
      round: 1,
      roundName: 'Semifinal #1',
      matchNumber: 1,
      status: teams[0] && teams[1] ? 'scheduled' : 'scheduled',
      scheduledTime: '10:00 AM',
      team1: teams[0] || { id: 't1', name: 'Seed 1 (Pending)', seed: 1, captainName: 'Captain TBD', members: [] },
      team2: teams[3] || teams[1] || { id: 't4', name: 'Seed 4 (Pending)', seed: 4, captainName: 'Captain TBD', members: [] },
      score1: 0,
      score2: 0,
    });

    generated.push({
      id: `gen_m2`,
      tournamentId: tournament.id,
      round: 1,
      roundName: 'Semifinal #2',
      matchNumber: 2,
      status: 'scheduled',
      scheduledTime: '11:30 AM',
      team1: teams[1] || { id: 't2', name: 'Seed 2 (Pending)', seed: 2, captainName: 'Captain TBD', members: [] },
      team2: teams[2] || { id: 't3', name: 'Seed 3 (Pending)', seed: 3, captainName: 'Captain TBD', members: [] },
      score1: 0,
      score2: 0,
    });

    // Championship Final
    generated.push({
      id: `gen_m3`,
      tournamentId: tournament.id,
      round: 2,
      roundName: 'Championship Final 🏆',
      matchNumber: 3,
      status: 'scheduled',
      scheduledTime: '2:00 PM',
      score1: 0,
      score2: 0,
    });

    return generated;
  }, [tournament]);

  // Group matches by round
  const roundNumbers = useMemo(() => {
    const rounds = Array.from(new Set(displayMatches.map((m) => m.round))).sort((a, b) => a - b);
    return rounds.length > 0 ? rounds : [1, 2];
  }, [displayMatches]);

  // Find championship winner team
  const championMatch = displayMatches.find(
    (m) => m.round === Math.max(...roundNumbers) && m.status === 'completed'
  );
  const championTeam = championMatch?.winnerId
    ? championMatch.team1?.id === championMatch.winnerId
      ? championMatch.team1
      : championMatch.team2
    : null;

  const triggerCelebration = () => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#a3e635', '#38bdf8', '#f59e0b', '#ec4899'],
    });
  };

  return (
    <div className="space-y-4">
      {/* Bracket Control Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-indigo-950/80 p-3 rounded-2xl border border-white/10">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-lime-400 text-black rounded-xl">
            <Swords className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-black uppercase italic text-white">Visual Tournament Bracket Tree</h4>
            <p className="text-[10px] text-indigo-300">
              {(tournament.registeredTeams || []).length} Registered Teams • {displayMatches.length} Bracket Matches
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 bg-indigo-900/80 p-1 rounded-xl border border-white/10 text-[11px] font-bold">
            <button
              type="button"
              onClick={() => setBracketMode('visual_tree')}
              className={`px-3 py-1 rounded-lg uppercase transition ${
                bracketMode === 'visual_tree'
                  ? 'bg-lime-400 text-black font-black shadow'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              Visual Tree 🌳
            </button>
            <button
              type="button"
              onClick={() => setBracketMode('compact_list')}
              className={`px-3 py-1 rounded-lg uppercase transition ${
                bracketMode === 'compact_list'
                  ? 'bg-lime-400 text-black font-black shadow'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              List Schedule 📋
            </button>
          </div>

          {championTeam && (
            <button
              type="button"
              onClick={triggerCelebration}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-lime-400 text-black font-black uppercase text-[10px] rounded-xl shadow-lg shadow-amber-400/20 transition flex items-center space-x-1"
            >
              <Crown className="w-3.5 h-3.5 fill-black" />
              <span>Celebrate Champion 🎉</span>
            </button>
          )}
        </div>
      </div>

      {/* VISUAL BRACKET TREE CANVAS */}
      {bracketMode === 'visual_tree' ? (
        <ExternalVisualBracket
          tournament={tournament}
          onEditMatchScore={(m) => onSetEditingMatch?.({ tournamentId: tournament.id, match: m })}
          onSelectTeam={(t) => setSelectedTeamModal(t)}
          onNominateMvp={(m) => {
            setMvpModalMatch(m);
            const team1Members = m.team1?.members?.map((mem: any) => typeof mem === 'string' ? mem : mem.name) || [];
            const team2Members = m.team2?.members?.map((mem: any) => typeof mem === 'string' ? mem : mem.name) || [];
            const candidates = [
              ...(team1Members.length ? team1Members : [m.team1?.captainName || m.team1?.name || 'Player 1']),
              ...(team2Members.length ? team2Members : [m.team2?.captainName || m.team2?.name || 'Player 2']),
            ];
            setSelectedMvpPlayer(candidates[0] || 'Standout Athlete');
          }}
        />
      ) : (
        /* Compact List View Schedule */
        <div className="space-y-3 bg-indigo-900/30 p-4 rounded-2xl border border-white/5">
          {displayMatches.map((m) => (
            <div
              key={m.id}
              className="p-3 bg-indigo-950 rounded-2xl border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-900 text-lime-400 font-black flex items-center justify-center text-xs shrink-0">
                  #{m.matchNumber}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-extrabold text-xs text-white">{m.roundName || `Round ${m.round}`}</span>
                    <span className="text-[10px] text-indigo-300 font-mono">• {m.scheduledTime || 'TBD'}</span>
                  </div>
                  <div className="text-xs text-indigo-200 mt-0.5 font-bold">
                    <span className={m.winnerId === m.team1?.id ? 'text-lime-400 font-extrabold' : ''}>
                      {m.team1?.name || 'TBD'} ({m.score1 ?? 0})
                    </span>{' '}
                    vs{' '}
                    <span className={m.winnerId === m.team2?.id ? 'text-lime-400 font-extrabold' : ''}>
                      {m.team2?.name || 'TBD'} ({m.score2 ?? 0})
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onSetEditingMatch({ tournamentId: tournament.id, match: m })}
                className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl text-xs font-bold border border-white/10 transition self-start sm:self-auto flex items-center space-x-1"
              >
                <Edit3 className="w-3.5 h-3.5 text-lime-400" />
                <span>Update Score</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {mvpToast && (
        <div className="p-3 bg-amber-400 text-black text-xs font-black uppercase rounded-2xl shadow-xl border border-amber-300 flex items-center justify-between font-mono animate-fadeIn">
          <span>{mvpToast}</span>
          <button onClick={() => setMvpToast(null)} className="text-black font-bold">✕</button>
        </div>
      )}

      {/* MVP Nomination Interactive Modal */}
      {mvpModalMatch && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-950 border-2 border-amber-400/50 w-full max-w-md rounded-[2.5rem] p-6 shadow-2xl relative space-y-5 text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2.5">
                <div className="p-2.5 bg-amber-400 text-black rounded-2xl shadow-md">
                  <Crown className="w-6 h-6 fill-black" />
                </div>
                <div>
                  <h3 className="text-lg font-black italic uppercase text-white">
                    Vote Match MVP 🌟
                  </h3>
                  <p className="text-xs text-indigo-300 font-semibold">
                    {mvpModalMatch.roundName || `Match #${mvpModalMatch.matchNumber}`} • Final Score: {mvpModalMatch.score1} - {mvpModalMatch.score2}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMvpModalMatch(null)}
                className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-black uppercase text-amber-300 tracking-wider block">
                Select Standout Nominee Player
              </label>

              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                {(() => {
                  const t1Members = mvpModalMatch.team1?.members?.map((mem: any) => typeof mem === 'string' ? mem : mem.name) || [];
                  const t2Members = mvpModalMatch.team2?.members?.map((mem: any) => typeof mem === 'string' ? mem : mem.name) || [];
                  const team1List = t1Members.length ? t1Members : [mvpModalMatch.team1?.captainName || `${mvpModalMatch.team1?.name} Star`];
                  const team2List = t2Members.length ? t2Members : [mvpModalMatch.team2?.captainName || `${mvpModalMatch.team2?.name} Star`];

                  return (
                    <>
                      <div className="text-[10px] font-black uppercase text-lime-400 pt-1">
                        {mvpModalMatch.team1?.name || 'Team 1'}
                      </div>
                      {team1List.map((pName, pIdx) => (
                        <button
                          key={`t1_${pIdx}`}
                          type="button"
                          onClick={() => setSelectedMvpPlayer(pName)}
                          className={`w-full p-3 rounded-2xl text-left font-bold text-xs flex items-center justify-between border transition ${
                            selectedMvpPlayer === pName
                              ? 'bg-amber-400 text-black border-amber-300 shadow-lg'
                              : 'bg-indigo-900/60 text-white border-white/10 hover:border-amber-400/50'
                          }`}
                        >
                          <span className="flex items-center space-x-2">
                            <span>{pName}</span>
                          </span>
                          {selectedMvpPlayer === pName && <Crown className="w-4 h-4 fill-black" />}
                        </button>
                      ))}

                      <div className="text-[10px] font-black uppercase text-cyan-400 pt-2">
                        {mvpModalMatch.team2?.name || 'Team 2'}
                      </div>
                      {team2List.map((pName, pIdx) => (
                        <button
                          key={`t2_${pIdx}`}
                          type="button"
                          onClick={() => setSelectedMvpPlayer(pName)}
                          className={`w-full p-3 rounded-2xl text-left font-bold text-xs flex items-center justify-between border transition ${
                            selectedMvpPlayer === pName
                              ? 'bg-amber-400 text-black border-amber-300 shadow-lg'
                              : 'bg-indigo-900/60 text-white border-white/10 hover:border-amber-400/50'
                          }`}
                        >
                          <span className="flex items-center space-x-2">
                            <span>{pName}</span>
                          </span>
                          {selectedMvpPlayer === pName && <Crown className="w-4 h-4 fill-black" />}
                        </button>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setMvpModalMatch(null)}
                className="flex-1 py-3 px-4 bg-indigo-900 text-indigo-300 font-bold text-xs uppercase rounded-2xl hover:bg-indigo-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleCastMvpVote(mvpModalMatch, selectedMvpPlayer)}
                className="flex-1 py-3 px-4 bg-amber-400 hover:bg-amber-300 text-black font-black italic text-xs uppercase rounded-2xl shadow-lg shadow-amber-400/20 transition flex items-center justify-center space-x-1.5"
              >
                <Crown className="w-4 h-4 fill-black shrink-0" />
                <span>Submit MVP Vote</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface TournamentLeaderboardSectionProps {
  tournaments: Tournament[];
  leaderboardMode: 'teams' | 'players';
  setLeaderboardMode: (mode: 'teams' | 'players') => void;
  selectedLeaderboardTournament: string;
  setSelectedLeaderboardTournament: (id: string) => void;
  leaderboardSearch: string;
  setLeaderboardSearch: (search: string) => void;
  selectedSport: string;
}

const TournamentLeaderboardSection: React.FC<TournamentLeaderboardSectionProps> = ({
  tournaments,
  leaderboardMode,
  setLeaderboardMode,
  selectedLeaderboardTournament,
  setSelectedLeaderboardTournament,
  leaderboardSearch,
  setLeaderboardSearch,
  selectedSport,
}) => {
  // Filter tournaments by selected sport and selected tournament ID
  const activeTournaments = useMemo(() => {
    return tournaments.filter((t) => {
      const matchSport = selectedSport === 'all' || t.sport === selectedSport;
      const matchId = selectedLeaderboardTournament === 'all' || t.id === selectedLeaderboardTournament;
      return matchSport && matchId;
    });
  }, [tournaments, selectedSport, selectedLeaderboardTournament]);

  // Aggregated Team Standings
  const teamStandings = useMemo(() => {
    const map: Record<string, {
      teamId: string;
      teamName: string;
      seed: number;
      tournamentTitle: string;
      sport: string;
      matchesPlayed: number;
      wins: number;
      losses: number;
      ptsScored: number;
      ptsAllowed: number;
    }> = {};

    activeTournaments.forEach((t) => {
      const tournamentTeams = (t as any).teams || [];
      tournamentTeams.forEach((team: any) => {
        const key = `${team.id}_${t.id}`;
        map[key] = {
          teamId: team.id,
          teamName: team.name,
          seed: team.seed || 1,
          tournamentTitle: t.title,
          sport: t.sport,
          matchesPlayed: 0,
          wins: 0,
          losses: 0,
          ptsScored: 0,
          ptsAllowed: 0,
        };
      });

      t.matches.forEach((m) => {
        if (m.status === 'completed' && m.team1 && m.team2) {
          const k1 = `${m.team1.id}_${t.id}`;
          const k2 = `${m.team2.id}_${t.id}`;

          const s1 = m.score1 || 0;
          const s2 = m.score2 || 0;

          if (map[k1]) {
            map[k1].matchesPlayed += 1;
            map[k1].ptsScored += s1;
            map[k1].ptsAllowed += s2;
            if (m.winnerId === m.team1.id || s1 > s2) map[k1].wins += 1;
            else map[k1].losses += 1;
          }

          if (map[k2]) {
            map[k2].matchesPlayed += 1;
            map[k2].ptsScored += s2;
            map[k2].ptsAllowed += s1;
            if (m.winnerId === m.team2.id || s2 > s1) map[k2].wins += 1;
            else map[k2].losses += 1;
          }
        }
      });
    });

    let list = Object.values(map);
    if (leaderboardSearch) {
      list = list.filter((t) =>
        t.teamName.toLowerCase().includes(leaderboardSearch.toLowerCase()) ||
        t.tournamentTitle.toLowerCase().includes(leaderboardSearch.toLowerCase())
      );
    }

    return list.sort((a, b) => {
      const winRateA = a.matchesPlayed > 0 ? a.wins / a.matchesPlayed : 0;
      const winRateB = b.matchesPlayed > 0 ? b.wins / b.matchesPlayed : 0;
      if (winRateB !== winRateA) return winRateB - winRateA;
      const diffA = a.ptsScored - a.ptsAllowed;
      const diffB = b.ptsScored - b.ptsAllowed;
      return diffB - diffA;
    });
  }, [activeTournaments, leaderboardSearch]);

  // Aggregated Player Leaders
  const playerLeaders = useMemo(() => {
    const map: Record<string, {
      playerName: string;
      teamName: string;
      tournamentTitle: string;
      sport: string;
      gamesPlayed: number;
      totalPts: number;
      totalKills: number;
      totalAces: number;
      totalBlocks: number;
      totalDigs: number;
      totalAst: number;
      totalReb: number;
      totalStl: number;
    }> = {};

    activeTournaments.forEach((t) => {
      t.matches.forEach((m) => {
        if (m.status === 'completed' && m.boxScores && m.boxScores.length > 0) {
          m.boxScores.forEach((bs) => {
            const key = bs.playerName;
            if (!map[key]) {
              map[key] = {
                playerName: bs.playerName,
                teamName: bs.teamName || 'Roster',
                tournamentTitle: t.title,
                sport: t.sport,
                gamesPlayed: 0,
                totalPts: 0,
                totalKills: 0,
                totalAces: 0,
                totalBlocks: 0,
                totalDigs: 0,
                totalAst: 0,
                totalReb: 0,
                totalStl: 0,
              };
            }
            map[key].gamesPlayed += 1;
            map[key].totalPts += bs.pts || 0;
            map[key].totalKills += bs.kills || 0;
            map[key].totalAces += bs.aces || 0;
            map[key].totalBlocks += bs.blocks || 0;
            map[key].totalDigs += bs.digs || 0;
            map[key].totalAst += bs.ast || 0;
            map[key].totalReb += bs.reb || 0;
            map[key].totalStl += bs.stl || 0;
          });
        }
      });
    });

    let list = Object.values(map);
    if (leaderboardSearch) {
      list = list.filter((p) =>
        p.playerName.toLowerCase().includes(leaderboardSearch.toLowerCase()) ||
        p.teamName.toLowerCase().includes(leaderboardSearch.toLowerCase())
      );
    }

    return list.sort((a, b) => {
      const scoringA = a.totalPts + a.totalKills;
      const scoringB = b.totalPts + b.totalKills;
      return scoringB - scoringA;
    });
  }, [activeTournaments, leaderboardSearch]);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-amber-950 via-indigo-950 to-amber-950 rounded-3xl border border-amber-400/30 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-2xl">
        <div>
          <div className="flex items-center space-x-2 text-amber-400 text-xs font-black uppercase tracking-widest mb-1">
            <Crown className="w-4 h-4" />
            <span>Official Tournament Standings & Player Records</span>
          </div>
          <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tight text-white">
            Tournament Leaderboard
          </h2>
          <p className="text-xs text-indigo-200/80 mt-1 font-medium max-w-xl">
            Real-time aggregated team standings, point differentials, and individual player box scores across active and historical tournament events.
          </p>
        </div>

        {/* Mode Switcher */}
        <div className="flex items-center space-x-2 bg-indigo-950/80 p-1.5 rounded-2xl border border-white/10 shrink-0">
          <button
            onClick={() => setLeaderboardMode('teams')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 ${
              leaderboardMode === 'teams'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Team Standings</span>
          </button>

          <button
            onClick={() => setLeaderboardMode('players')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 ${
              leaderboardMode === 'players'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Star className="w-4 h-4" />
            <span>Player Stat Leaders</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
          <input
            type="text"
            placeholder={leaderboardMode === 'teams' ? "Search team name or tournament..." : "Search player name or team..."}
            value={leaderboardSearch}
            onChange={(e) => setLeaderboardSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={selectedLeaderboardTournament}
            onChange={(e) => setSelectedLeaderboardTournament(e.target.value)}
            className="p-2 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-400 max-w-xs"
          >
            <option value="all">All Tournaments ({tournaments.length})</option>
            {tournaments.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} ({t.sport})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content Table / Cards */}
      {leaderboardMode === 'teams' ? (
        <div className="space-y-3">
          {teamStandings.length === 0 ? (
            <div className="p-8 text-center bg-indigo-950/80 rounded-3xl border border-white/10 text-xs text-indigo-300">
              No tournament teams found matching filter criteria.
            </div>
          ) : (
            <div className="bg-indigo-950/90 rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-semibold">
                  <thead className="bg-indigo-900/90 text-indigo-200 uppercase font-black tracking-wider text-[10px] border-b border-white/10">
                    <tr>
                      <th className="p-4">Rank</th>
                      <th className="p-4">Team Name</th>
                      <th className="p-4">Tournament</th>
                      <th className="p-4 text-center">Matches</th>
                      <th className="p-4 text-center">W - L</th>
                      <th className="p-4 text-center">Win %</th>
                      <th className="p-4 text-center">PTS Scored</th>
                      <th className="p-4 text-center">PTS Allowed</th>
                      <th className="p-4 text-center">Diff (+/-)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {teamStandings.map((t, idx) => {
                      const total = t.wins + t.losses;
                      const winRate = total > 0 ? Math.round((t.wins / total) * 100) : 0;
                      const diff = t.ptsScored - t.ptsAllowed;

                      return (
                        <tr key={`${t.teamId}_${idx}`} className="hover:bg-white/5 transition">
                          <td className="p-4 font-mono font-black text-sm">
                            {idx === 0 ? (
                              <span className="px-2 py-1 bg-amber-400 text-black rounded-lg font-bold">🥇 #1</span>
                            ) : idx === 1 ? (
                              <span className="px-2 py-1 bg-slate-300 text-black rounded-lg font-bold">🥈 #2</span>
                            ) : idx === 2 ? (
                              <span className="px-2 py-1 bg-amber-700 text-white rounded-lg font-bold">🥉 #3</span>
                            ) : (
                              <span className="text-indigo-300">#{idx + 1}</span>
                            )}
                          </td>
                          <td className="p-4 font-black italic uppercase text-white text-sm">
                            {t.teamName}
                          </td>
                          <td className="p-4 text-indigo-200 text-xs">
                            <span className="px-2 py-0.5 bg-indigo-900 rounded-md font-mono text-[10px] text-amber-300 border border-white/10 block w-fit">
                              {t.tournamentTitle}
                            </span>
                          </td>
                          <td className="p-4 text-center font-mono font-bold text-white">{t.matchesPlayed}</td>
                          <td className="p-4 text-center font-mono font-bold text-lime-400">{t.wins} - {t.losses}</td>
                          <td className="p-4 text-center font-mono font-black text-amber-400">{winRate}%</td>
                          <td className="p-4 text-center font-mono font-bold text-white">{t.ptsScored}</td>
                          <td className="p-4 text-center font-mono text-indigo-300">{t.ptsAllowed}</td>
                          <td className={`p-4 text-center font-mono font-black ${diff >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {diff >= 0 ? `+${diff}` : diff}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {playerLeaders.length === 0 ? (
            <div className="p-8 text-center bg-indigo-950/80 rounded-3xl border border-white/10 text-xs text-indigo-300">
              No player box score records logged in tournament matches yet.
            </div>
          ) : (
            <div className="bg-indigo-950/90 rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-semibold">
                  <thead className="bg-indigo-900/90 text-indigo-200 uppercase font-black tracking-wider text-[10px] border-b border-white/10">
                    <tr>
                      <th className="p-4">Rank</th>
                      <th className="p-4">Player Name</th>
                      <th className="p-4">Team</th>
                      <th className="p-4 text-center">Games</th>
                      <th className="p-4 text-center">PTS / Kills</th>
                      <th className="p-4 text-center">Assists</th>
                      <th className="p-4 text-center">Reb / Digs</th>
                      <th className="p-4 text-center">Blocks / Stl</th>
                      <th className="p-4 text-center">MVP Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {playerLeaders.map((p, idx) => {
                      const mvpScore = Math.round((p.totalPts * 1.5) + (p.totalKills * 1.5) + (p.totalAst * 2) + ((p.totalReb + p.totalDigs) * 1.2) + ((p.totalBlocks + p.totalStl) * 2));

                      return (
                        <tr key={`${p.playerName}_${idx}`} className="hover:bg-white/5 transition">
                          <td className="p-4 font-mono font-black text-sm">
                            {idx === 0 ? (
                              <span className="px-2 py-1 bg-amber-400 text-black rounded-lg font-bold">🥇 #1</span>
                            ) : idx === 1 ? (
                              <span className="px-2 py-1 bg-slate-300 text-black rounded-lg font-bold">🥈 #2</span>
                            ) : idx === 2 ? (
                              <span className="px-2 py-1 bg-amber-700 text-white rounded-lg font-bold">🥉 #3</span>
                            ) : (
                              <span className="text-indigo-300">#{idx + 1}</span>
                            )}
                          </td>
                          <td className="p-4 font-black italic uppercase text-white text-sm flex items-center space-x-2">
                            <span>{p.playerName}</span>
                            {idx === 0 && <span className="text-amber-400">👑</span>}
                          </td>
                          <td className="p-4 text-indigo-200 text-xs">{p.teamName}</td>
                          <td className="p-4 text-center font-mono font-bold text-white">{p.gamesPlayed}</td>
                          <td className="p-4 text-center font-mono font-black text-lime-400">
                            {p.totalPts + p.totalKills}
                          </td>
                          <td className="p-4 text-center font-mono font-bold text-cyan-300">{p.totalAst}</td>
                          <td className="p-4 text-center font-mono font-bold text-indigo-200">{p.totalReb + p.totalDigs}</td>
                          <td className="p-4 text-center font-mono font-bold text-purple-300">{p.totalBlocks + p.totalStl}</td>
                          <td className="p-4 text-center font-mono font-black text-amber-400 bg-amber-400/10 rounded-xl">
                            {mvpScore}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TournamentsView: React.FC<TournamentsViewProps> = ({
  tournaments,
  courts,
  user,
  onUpdateUser,
  onCreateTournament,
  onRegisterTeam,
  onUpdateMatchScore,
  onUpdatePrizePool,
  onSelectCourtOnMap,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSport, setSelectedSport] = useState<SportType | 'all'>(user?.primarySport || 'all');
  const [selectedStatus, setSelectedStatus] = useState<TournamentStatus | 'all'>('all');

  // Registration Location & Local Filtering State
  const [locationFilter, setLocationFilter] = useState<'all' | 'my_state' | 'my_city'>('all');
  const [maxDistanceMiles, setMaxDistanceMiles] = useState<number>(50);
  const [stateSearch, setStateSearch] = useState<string>('all');
  const [citySearch, setCitySearch] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [userRegState, setUserRegState] = useState<string>(user?.registeredState || 'NY');
  const [userRegCity, setUserRegCity] = useState<string>(user?.registeredCity || 'New York');

  // Selected Tournament for detailed Bracket/Hub view
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(
    tournaments.length > 0 ? tournaments[0].id : null
  );

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState<string | null>(null); // tournamentId
  const [editingMatch, setEditingMatch] = useState<{ tournamentId: string; match: TournamentMatch } | null>(null);
  const [editingPrizePoolTournament, setEditingPrizePoolTournament] = useState<Tournament | null>(null);
  const [editPrizePoolInput, setEditPrizePoolInput] = useState('');

  // Download Printable Bracket Export Function
  const handleDownloadBracket = (t: Tournament) => {
    setPdfReportTournament(t);
    triggerToast(`Generated Official Branded PDF Report Preview for ${t.title}! 📄`);
  };

  // New Tournament Form State
  const [newTitle, setNewTitle] = useState('');
  const [newCourtId, setNewCourtId] = useState(courts[0]?.id || '');
  const [newSport, setNewSport] = useState<SportType>('basketball');
  const [newFormat, setNewFormat] = useState<TournamentFormat>('single_elimination');
  const [newMaxTeams, setNewMaxTeams] = useState(4);
  const [newStartDate, setNewStartDate] = useState('2026-08-25');
  const [newEndDate, setNewEndDate] = useState('2026-08-26');
  const [newPrizePool, setNewPrizePool] = useState('$1,000 Total Rewards');
  const [newPrizeFirst, setNewPrizeFirst] = useState('$500 Cash');
  const [newPrizeSecond, setNewPrizeSecond] = useState('$250 Cash');
  const [newPrizeThird, setNewPrizeThird] = useState('$150 Gear Voucher');
  const [newPrizeMvp, setNewPrizeMvp] = useState('$100 Pro Shop Credit');
  const [newEntryFee, setNewEntryFee] = useState('Free');
  const [newDescription, setNewDescription] = useState('');
  const [newRules, setNewRules] = useState('');

  // Team Registration Form State
  const [regTeamName, setRegTeamName] = useState('');
  const [regCaptainName, setRegCaptainName] = useState('');
  const [regMember1, setRegMember1] = useState('');
  const [regMember2, setRegMember2] = useState('');
  const [regMember3, setRegMember3] = useState('');

  // Match Editing Score State
  const [editScore1, setEditScore1] = useState(0);
  const [editScore2, setEditScore2] = useState(0);

  // View Tab State ('current' vs 'history' vs 'calendar' vs 'leaderboard')
  const [viewTab, setViewTab] = useState<'current' | 'history' | 'calendar' | 'leaderboard'>('current');
  const [leaderboardMode, setLeaderboardMode] = useState<'teams' | 'players'>('teams');
  const [selectedLeaderboardTournament, setSelectedLeaderboardTournament] = useState<string>('all');
  const [leaderboardSearch, setLeaderboardSearch] = useState<string>('');

  // New Features States: Live Feed Drawer, Share Modal, Tooltips, Toast
  const [isLiveDrawerOpen, setIsLiveDrawerOpen] = useState(false);
  const [liveFeedFilter, setLiveFeedFilter] = useState<'all' | 'results' | 'mvp' | 'registrations'>('all');
  const [feedReactions, setFeedReactions] = useState<Record<string, number>>({});

  const [shareModalTournament, setShareModalTournament] = useState<Tournament | null>(null);
  const [sharePostText, setSharePostText] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  // 1. Admin Bulk CSV/Paste Team Registration State
  const [showBulkModal, setShowBulkModal] = useState<string | null>(null);
  const [bulkInputText, setBulkInputText] = useState('');
  const [bulkTab, setBulkTab] = useState<'paste' | 'upload'>('paste');
  const [bulkParsedTeams, setBulkParsedTeams] = useState<Array<{
    name: string;
    captainName: string;
    member1: string;
    member2: string;
    valid: boolean;
    error?: string;
  }>>([]);

  // 2. Compact Scrolling Live Scores Ticker State
  const [isTickerPaused, setIsTickerPaused] = useState(false);

  // 3. Branded PDF Bracket Report Modal State
  const [pdfReportTournament, setPdfReportTournament] = useState<Tournament | null>(null);

  // 4. Tournament Courts Mini-Map State
  const [selectedMapCourtId, setSelectedMapCourtId] = useState<string | null>(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [mapFilterSport, setMapFilterSport] = useState<SportType | 'all'>('all');

  // 5. Tournament Hub Sub-Tabs State ('bracket' | 'radar' | 'roster')
  const [hubSubTab, setHubSubTab] = useState<'bracket' | 'radar' | 'roster'>('bracket');

  // 6. Radar Chart Side-by-Side Team Comparison State
  const [compareTeam1Id, setCompareTeam1Id] = useState<string>('');
  const [compareTeam2Id, setCompareTeam2Id] = useState<string>('');

  // 7. Real-Time Tournament Status Board Alerts State
  const [statusBoardFilter, setStatusBoardFilter] = useState<'all' | 'match' | 'prize' | 'deadline' | 'payment'>('all');
  const [statusBoardAlerts, setStatusBoardAlerts] = useState<Array<{
    id: string;
    tournamentId: string;
    tournamentTitle: string;
    type: 'match' | 'prize' | 'deadline' | 'payment';
    title: string;
    message: string;
    timestamp: string;
    badgeColor: string;
  }>>([
    {
      id: 'sb_1',
      tournamentId: 'tourney_1',
      tournamentTitle: 'Rucker Park Summer 3v3 Showdown',
      type: 'match',
      title: '⚡ Semifinal Finalized',
      message: 'Harlem Kings defeated The Cage Vipers (21 - 15) in Semifinal #1. Marcus Vance scored 14 PTS.',
      timestamp: 'Just now',
      badgeColor: 'bg-lime-400 text-black',
    },
    {
      id: 'sb_2',
      tournamentId: 'tourney_1',
      tournamentTitle: 'Rucker Park Summer 3v3 Showdown',
      type: 'prize',
      title: '🏆 Prize Pool Boosted',
      message: 'Purse increased to $1,000 Cash + Championship Ring! Sponsored by Harlem Ballers Club.',
      timestamp: '15 mins ago',
      badgeColor: 'bg-amber-400 text-black',
    },
    {
      id: 'sb_3',
      tournamentId: 'tourney_1',
      tournamentTitle: 'Rucker Park Summer 3v3 Showdown',
      type: 'deadline',
      title: '⏰ Registration Deadline Approaching',
      message: 'Only 1 team slot left! Registration deadline closes Aug 14 at 6:00 PM.',
      timestamp: '1 hour ago',
      badgeColor: 'bg-rose-500 text-white',
    },
    {
      id: 'sb_4',
      tournamentId: 'tourney_1',
      tournamentTitle: 'Rucker Park Summer 3v3 Showdown',
      type: 'payment',
      title: '💳 Entry Fee Paid & Verified',
      message: 'Harlem Kings paid $20 entry fee. Seeding #1 locked in.',
      timestamp: '2 hours ago',
      badgeColor: 'bg-cyan-400 text-black',
    },
  ]);

  // 8. Simulated Entry Fee Payment Modal State
  const [payingTeamData, setPayingTeamData] = useState<{ tournament: Tournament; team: TournamentTeam } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'apple' | 'cashapp'>('card');
  const [payCardName, setPayCardName] = useState('');
  const [payCardNumber, setPayCardNumber] = useState('');
  const [payCardExp, setPayCardExp] = useState('');
  const [payCardCvc, setPayCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handleSimulatedPayment = (e?: React.FormEvent | any) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!payingTeamData) return;

    setIsProcessingPayment(true);

    setTimeout(() => {
      const receiptId = `RCP-PAY-${Math.floor(10000 + Math.random() * 90000)}`;
      const nowIso = new Date().toISOString();

      payingTeamData.team.paymentStatus = 'Paid';
      payingTeamData.team.paymentReceiptId = receiptId;
      payingTeamData.team.paidAt = nowIso;

      try {
        confetti({
          particleCount: 85,
          spread: 75,
          origin: { y: 0.6 },
          colors: ['#a3e635', '#38bdf8', '#f59e0b', '#ec4899'],
        });
      } catch (err) {
        console.log('Confetti fired', err);
      }

      const newAlert = {
        id: `sb_${Date.now()}`,
        tournamentId: payingTeamData.tournament.id,
        tournamentTitle: payingTeamData.tournament.title,
        type: 'payment' as const,
        title: '💳 Entry Fee Paid & Verified',
        message: `${payingTeamData.team.name} paid $${payingTeamData.tournament.entryFee || '20'} entry fee. Registration status updated to Paid & Verified!`,
        timestamp: 'Just now',
        badgeColor: 'bg-emerald-400 text-black',
      };
      setStatusBoardAlerts((prev) => [newAlert, ...prev]);

      triggerToast(`Payment Verified! ${payingTeamData.team.name} is now Paid & Confirmed for ${payingTeamData.tournament.title}! 🎟️`);

      setIsProcessingPayment(false);
      setPayingTeamData(null);
      setPayCardName('');
      setPayCardNumber('');
      setPayCardExp('');
      setPayCardCvc('');
    }, 1200);
  };

  const [hoveredMvpTourneyId, setHoveredMvpTourneyId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // CSV Parsing & Bulk Import Handlers
  const handleParseBulkText = (text: string) => {
    setBulkInputText(text);
    if (!text.trim()) {
      setBulkParsedTeams([]);
      return;
    }
    const lines = text.split('\n');
    const parsed: Array<{
      name: string;
      captainName: string;
      member1: string;
      member2: string;
      valid: boolean;
      error?: string;
    }> = [];

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const lower = trimmed.toLowerCase();
      if (idx === 0 && (lower.includes('team') || lower.includes('captain') || lower.includes('name'))) {
        return; // Header line skip
      }

      const parts = trimmed.split(/,|\t/).map((p) => p.trim());
      const name = parts[0] || '';
      const captainName = parts[1] || (name ? `${name} Capt` : 'Captain');
      const member1 = parts[2] || '';
      const member2 = parts[3] || '';

      if (!name) {
        parsed.push({ name: '', captainName: '', member1: '', member2: '', valid: false, error: 'Team name required' });
      } else {
        parsed.push({ name, captainName, member1, member2, valid: true });
      }
    });

    setBulkParsedTeams(parsed);
  };

  const handleCsvFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        handleParseBulkText(text);
        triggerToast(`Loaded CSV file: ${file.name}`);
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadCsvTemplate = () => {
    const csvContent = "Team Name, Captain Name, Player 1, Player 2\nVenom Elite, Marcus Vance, J. Cole, D. Booker\nHarlem Kings, Kevin Durant, Steph Curry, Klay Thompson\nBrooklyn Nets, Kyrie Irving, James Harden, Joe Harris\n";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'playground_tournament_teams_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    triggerToast('Downloaded CSV Template! 📄');
  };

  const handleConfirmBulkRegister = (tournamentId: string) => {
    const validTeams = bulkParsedTeams.filter((t) => t.valid);
    if (validTeams.length === 0) {
      triggerToast('No valid teams to register.');
      return;
    }

    const targetTourney = tournaments.find((t) => t.id === tournamentId);
    const currentCount = targetTourney?.registeredTeams.length || 0;

    validTeams.forEach((teamData, index) => {
      const membersList = [teamData.captainName];
      if (teamData.member1) membersList.push(teamData.member1);
      if (teamData.member2) membersList.push(teamData.member2);

      const newTeam: TournamentTeam = {
        id: `team_bulk_${Date.now()}_${index}`,
        name: teamData.name,
        captainName: teamData.captainName,
        seed: currentCount + index + 1,
        members: membersList.map((m, mIdx) => ({ id: `mem_${mIdx}`, name: m })),
      };

      onRegisterTeam(tournamentId, newTeam);
    });

    triggerToast(`Successfully registered ${validTeams.length} teams in bulk! 🚀`);
    setShowBulkModal(null);
    setBulkInputText('');
    setBulkParsedTeams([]);
  };

  // Generate Real-time Live Tournament Feed Items
  const liveFeedItems = useMemo(() => {
    const items: Array<{
      id: string;
      type: 'results' | 'mvp' | 'registrations';
      title: string;
      description: string;
      timestamp: string;
      sport: SportType;
      tournamentId: string;
      tournamentTitle: string;
    }> = [];

    tournaments.forEach((t) => {
      // Team Registration events
      (t.registeredTeams || []).forEach((team, idx) => {
        items.push({
          id: `reg_${t.id}_${team.id}`,
          type: 'registrations',
          title: '📝 New Team Registered',
          description: `"${team.name}" registered for ${t.title} (Captain: ${team.captainName})`,
          timestamp: `${(idx + 1) * 14}m ago`,
          sport: t.sport,
          tournamentId: t.id,
          tournamentTitle: t.title,
        });
      });

      // Match Result events
      (t.matches || []).forEach((m) => {
        if (m.status === 'completed' && m.team1 && m.team2) {
          items.push({
            id: `match_${t.id}_${m.id}`,
            type: 'results',
            title: '🏆 Match Score Posted',
            description: `Match #${m.matchNumber}: ${m.team1.name} (${m.score1}) vs ${m.team2.name} (${m.score2}). Winner: ${m.winnerId === m.team1.id ? m.team1.name : m.team2.name}`,
            timestamp: `${m.matchNumber * 12}m ago`,
            sport: t.sport,
            tournamentId: t.id,
            tournamentTitle: t.title,
          });
        }
      });

      // MVP Announcement events
      const mvp = computeTournamentMVP(t);
      if (mvp && (t.status === 'completed' || t.mvpPlayerName)) {
        items.push({
          id: `mvp_${t.id}`,
          type: 'mvp',
          title: '🎖️ Tournament MVP Awarded',
          description: `${mvp.playerName} named Official MVP of ${t.title}! (${mvp.totalPts} Total PTS in ${mvp.matchesCount} matches)`,
          timestamp: '1h ago',
          sport: t.sport,
          tournamentId: t.id,
          tournamentTitle: t.title,
        });
      }
    });

    return items;
  }, [tournaments]);

  const filteredLiveFeed = useMemo(() => {
    if (liveFeedFilter === 'all') return liveFeedItems;
    return liveFeedItems.filter((item) => item.type === liveFeedFilter);
  }, [liveFeedItems, liveFeedFilter]);

  // Calendar Tournaments Schedule Filter
  const calendarTournaments = useMemo(() => {
    return tournaments
      .filter((t) => t.status !== 'completed')
      .filter((t) => selectedSport === 'all' || t.sport === selectedSport)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [tournaments, selectedSport]);

  // Collapsible Match Box Score State
  const [expandedMatchIds, setExpandedMatchIds] = useState<Record<string, boolean>>({});

  // Hovered Match ID for bracket connector path glow effect
  const [hoveredMatchId, setHoveredMatchId] = useState<string | null>(null);

  // 2. All Matches for Live Scores Ticker
  const allLiveMatches = useMemo(() => {
    const list: Array<{ match: TournamentMatch; tournament: Tournament }> = [];
    tournaments.forEach((t) => {
      (t.matches || []).forEach((m) => {
        list.push({ match: m, tournament: t });
      });
    });

    // Prioritize in_progress first, then completed, then scheduled
    return list.sort((a, b) => {
      const rank = { in_progress: 0, completed: 1, scheduled: 2 };
      return (rank[a.match.status] ?? 3) - (rank[b.match.status] ?? 3);
    });
  }, [tournaments]);

  // 4. Map Filtered Courts
  const filteredMapCourts = useMemo(() => {
    return courts.filter((c) => mapFilterSport === 'all' || c.sport === mapFilterSport);
  }, [courts, mapFilterSport]);

  const selectedCourtObj = useMemo(() => {
    return courts.find((c) => c.id === selectedMapCourtId) || null;
  }, [courts, selectedMapCourtId]);

  // Compute Team Win Rates across tournaments for History Section Recharts Chart
  const teamWinRateData = useMemo(() => {
    const statsMap: Record<string, { teamName: string; wins: number; losses: number; matches: number }> = {};

    tournaments.forEach((t) => {
      (t.matches || []).forEach((m) => {
        if (m.status === 'completed' && m.team1 && m.team2 && m.winnerId) {
          const t1 = m.team1.name;
          const t2 = m.team2.name;

          if (!statsMap[t1]) statsMap[t1] = { teamName: t1, wins: 0, losses: 0, matches: 0 };
          if (!statsMap[t2]) statsMap[t2] = { teamName: t2, wins: 0, losses: 0, matches: 0 };

          statsMap[t1].matches += 1;
          statsMap[t2].matches += 1;

          if (m.winnerId === m.team1.id) {
            statsMap[t1].wins += 1;
            statsMap[t2].losses += 1;
          } else if (m.winnerId === m.team2.id) {
            statsMap[t2].wins += 1;
            statsMap[t1].losses += 1;
          }
        }
      });

      // Ensure all registered teams in past/completed tournaments are represented
      (t.registeredTeams || []).forEach((team) => {
        if (!statsMap[team.name]) {
          statsMap[team.name] = { teamName: team.name, wins: 1, losses: 1, matches: 2 };
        }
      });
    });

    return Object.values(statsMap)
      .map((s) => ({
        teamName: s.teamName,
        wins: s.wins,
        losses: s.losses,
        totalMatches: s.matches,
        winRate: s.matches > 0 ? Math.round((s.wins / s.matches) * 100) : 0,
      }))
      .sort((a, b) => b.winRate - a.winRate || b.wins - a.wins);
  }, [tournaments]);

  const toggleMatchExpanded = (matchId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedMatchIds((prev) => ({
      ...prev,
      [matchId]: !prev[matchId],
    }));
  };

  // Filtered Tournaments
  const filteredTournaments = tournaments.filter((t) => {
    const matchesSearch =
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.courtName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.organizerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.city && t.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.state && t.state.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSport = selectedSport === 'all' || t.sport === selectedSport;
    const matchesStatus = selectedStatus === 'all' || t.status === selectedStatus;

    let matchesLocation = true;
    if (locationFilter === 'my_state') {
      const uState = (user?.registeredState || userRegState || '').toLowerCase();
      const tState = (t.state || '').toLowerCase();
      matchesLocation = tState === uState || t.address.toLowerCase().includes(uState);
    } else if (locationFilter === 'my_city') {
      const uCity = (user?.registeredCity || userRegCity || '').toLowerCase();
      const tCity = (t.city || '').toLowerCase();
      matchesLocation = tCity.includes(uCity) || t.address.toLowerCase().includes(uCity);
    }

    if (stateSearch !== 'all') {
      const sQuery = stateSearch.toLowerCase();
      const tState = (t.state || '').toLowerCase();
      if (tState !== sQuery && !t.address.toLowerCase().includes(sQuery)) {
        matchesLocation = false;
      }
    }

    if (citySearch.trim()) {
      const cQuery = citySearch.trim().toLowerCase();
      const tCity = (t.city || '').toLowerCase();
      if (!tCity.includes(cQuery) && !t.address.toLowerCase().includes(cQuery)) {
        matchesLocation = false;
      }
    }

    // Distance radius calculation from user's registered city
    let estMiles = 75;
    const uCity = (user?.registeredCity || userRegCity || 'New York').toLowerCase();
    const uState = (user?.registeredState || userRegState || 'NY').toLowerCase();
    const matchedCourt = courts.find((c) => c.id === t.courtId || c.name.toLowerCase() === t.courtName.toLowerCase());

    if (matchedCourt && matchedCourt.distanceKm !== undefined) {
      estMiles = matchedCourt.distanceKm * 0.621371;
    } else if (t.city && t.city.toLowerCase().includes(uCity)) {
      estMiles = 3;
    } else if (t.state && t.state.toLowerCase() === uState) {
      estMiles = 22;
    } else if (t.address.toLowerCase().includes(uCity)) {
      estMiles = 5;
    }

    const matchesDistance = maxDistanceMiles >= 100 || estMiles <= maxDistanceMiles;

    return matchesSearch && matchesSport && matchesStatus && matchesLocation && matchesDistance;
  });

  const activeTournament = tournaments.find((t) => t.id === selectedTournamentId) || filteredTournaments[0] || tournaments[0];
  const activeMVP = activeTournament ? computeTournamentMVP(activeTournament) : null;

  // Handle New Tournament Creation
  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    const selectedCourtObj = courts.find((c) => c.id === newCourtId) || courts[0];

    // Auto generate initial round of matches for 4 teams format
    const initialMatches: TournamentMatch[] = [];
    if (newMaxTeams === 4) {
      initialMatches.push({
        id: `m_${Date.now()}_1`,
        tournamentId: '',
        round: 1,
        roundName: 'Semifinal #1',
        matchNumber: 1,
        status: 'scheduled',
        scheduledTime: '10:00 AM',
        courtName: `${selectedCourtObj?.name || 'Court'} - Main 1`,
      });
      initialMatches.push({
        id: `m_${Date.now()}_2`,
        tournamentId: '',
        round: 1,
        roundName: 'Semifinal #2',
        matchNumber: 2,
        status: 'scheduled',
        scheduledTime: '11:15 AM',
        courtName: `${selectedCourtObj?.name || 'Court'} - Main 1`,
      });
      initialMatches.push({
        id: `m_${Date.now()}_3`,
        tournamentId: '',
        round: 2,
        roundName: 'Championship Final 🏆',
        matchNumber: 3,
        status: 'scheduled',
        scheduledTime: '2:00 PM',
        courtName: `${selectedCourtObj?.name || 'Court'} - Arena`,
      });
    }

    const created: Tournament = {
      id: `tourney_${Date.now()}`,
      title: newTitle,
      sport: newSport,
      courtId: selectedCourtObj?.id || 'court_1',
      courtName: selectedCourtObj?.name || 'Local Playground',
      address: selectedCourtObj?.address || 'Community Park',
      startDate: newStartDate,
      endDate: newEndDate,
      format: newFormat,
      status: 'registration_open',
      organizerName: 'Playground Admin',
      maxTeams: Number(newMaxTeams),
      prizePool: newPrizePool || 'Bragging Rights',
      prizeDistribution: {
        firstPlace: newPrizeFirst,
        secondPlace: newPrizeSecond,
        thirdPlace: newPrizeThird,
        mvpReward: newPrizeMvp,
      },
      entryFee: newEntryFee || 'Free',
      description: newDescription || 'Official playground tournament hosted on local courts.',
      rules: newRules || 'Standard playground street rules apply. Call your own fouls with respect.',
      registeredTeams: [],
      matches: initialMatches,
    };

    onCreateTournament(created);
    setSelectedTournamentId(created.id);
    setShowCreateModal(false);

    // Reset Form
    setNewTitle('');
    setNewDescription('');
    setNewRules('');
  };

  // Handle Registration Submit
  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!showRegisterModal || !regTeamName.trim() || !regCaptainName.trim()) return;

    const membersList = [regCaptainName];
    if (regMember1.trim()) membersList.push(regMember1.trim());
    if (regMember2.trim()) membersList.push(regMember2.trim());
    if (regMember3.trim()) membersList.push(regMember3.trim());

    const newTeam: TournamentTeam = {
      id: `team_${Date.now()}`,
      name: regTeamName,
      captainName: regCaptainName,
      seed: (activeTournament?.registeredTeams.length || 0) + 1,
      members: membersList.map((m, idx) => ({ id: `mem_${idx}`, name: m })),
    };

    onRegisterTeam(showRegisterModal, newTeam);
    setShowRegisterModal(null);
    setRegTeamName('');
    setRegCaptainName('');
    setRegMember1('');
    setRegMember2('');
    setRegMember3('');
  };

  // Handle Score Save
  const handleScoreSave = () => {
    if (!editingMatch) return;

    let winnerId: string | undefined = undefined;
    if (editScore1 > editScore2 && editingMatch.match.team1) {
      winnerId = editingMatch.match.team1.id;
    } else if (editScore2 > editScore1 && editingMatch.match.team2) {
      winnerId = editingMatch.match.team2.id;
    }

    onUpdateMatchScore(editingMatch.tournamentId, editingMatch.match.id, editScore1, editScore2, winnerId);
    setEditingMatch(null);
  };

  return (
    <div className="space-y-6 text-white pb-12">
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-900 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="absolute -right-8 -bottom-8 opacity-10 pointer-events-none">
          <Trophy className="w-72 h-72 text-lime-400" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-lime-400 text-xs font-black uppercase tracking-widest mb-1">
              <Swords className="w-4 h-4" />
              <span>Court Cups & Leagues</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black italic uppercase tracking-tight">
              Tournaments & Brackets
            </h1>
            <p className="text-indigo-200/80 text-xs md:text-sm mt-1 max-w-xl font-medium">
              Join bracket-style street elimination or round-robin group cups. Track live scores, seedings, and championship glory.
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="self-start md:self-auto px-5 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-2xl shadow-lg shadow-lime-400/20 transition transform active:scale-95 flex items-center space-x-2 text-sm uppercase tracking-wider"
          >
            <Plus className="w-5 h-5 stroke-[2.5]" />
            <span>Organize Tournament</span>
          </button>
        </div>
      </div>

      {/* REGISTRATION LOCATION & LOCAL EXPERIENCE BAR */}
      <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-cyan-400 text-black rounded-xl">
            <MapPin className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] uppercase font-black tracking-widest text-cyan-300">
                REGISTERED ATHLETE LOCATION
              </span>
              <span className="px-2 py-0.5 bg-cyan-400/20 text-cyan-300 text-[10px] font-bold rounded-full">
                LOCAL MATCHING ACTIVE 📍
              </span>
            </div>
            <div className="text-sm font-black italic text-white flex items-center gap-2">
              <span>{user?.registeredCity || userRegCity}, {user?.registeredState || userRegState}</span>
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="text-xs text-lime-400 underline hover:text-lime-300 font-bold ml-2"
              >
                Change Registration City/State
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase font-black tracking-widest text-lime-400 mr-1">
            Regional View:
          </span>
          <button
            type="button"
            onClick={() => { setLocationFilter('my_city'); setStateSearch('all'); setCitySearch(''); }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition ${
              locationFilter === 'my_city'
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/80 text-indigo-200 hover:text-white'
            }`}
          >
            📍 Local ({user?.registeredCity || userRegCity})
          </button>
          <button
            type="button"
            onClick={() => { setLocationFilter('my_state'); setStateSearch('all'); setCitySearch(''); }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition ${
              locationFilter === 'my_state'
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/80 text-indigo-200 hover:text-white'
            }`}
          >
            🏛️ Statewide ({user?.registeredState || userRegState})
          </button>
          <button
            type="button"
            onClick={() => { setLocationFilter('all'); setStateSearch('all'); setCitySearch(''); }}
            className={`px-3 py-1.5 rounded-xl text-xs font-black transition ${
              locationFilter === 'all' && stateSearch === 'all' && !citySearch
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/80 text-indigo-200 hover:text-white'
            }`}
          >
            🇺🇸 National (All US)
          </button>

          {/* Distance Radius Range Slider */}
          <div className="flex items-center space-x-2.5 bg-indigo-900/80 px-3 py-1.5 rounded-xl border border-white/10 ml-auto">
            <span className="text-[10px] uppercase font-black tracking-wider text-lime-400 shrink-0">
              Radius: <span className="font-mono text-white">{maxDistanceMiles >= 100 ? 'Any' : `${maxDistanceMiles} mi`}</span>
            </span>
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={maxDistanceMiles}
              onChange={(e) => setMaxDistanceMiles(Number(e.target.value))}
              className="w-24 sm:w-32 h-1.5 bg-indigo-950 rounded-lg appearance-none cursor-pointer accent-lime-400 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* COMPACT SCROLLING LIVE SCORES TICKER */}
      {allLiveMatches.length > 0 && (
        <div className="bg-slate-950/95 backdrop-blur-md border border-lime-400/40 rounded-2xl p-2.5 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-3 relative overflow-hidden">
          {/* Left Ticker Badge */}
          <div className="flex items-center space-x-2 shrink-0">
            <div className="px-3 py-1 bg-gradient-to-r from-rose-500 via-amber-500 to-lime-400 text-black font-black text-[10px] uppercase italic rounded-xl flex items-center space-x-1.5 shadow-md">
              <Radio className="w-3.5 h-3.5 text-black animate-pulse" />
              <span>LIVE SCORES TICKER</span>
            </div>
            <span className="text-[10px] text-indigo-300 font-mono font-bold hidden sm:inline">
              {allLiveMatches.filter((m) => m.match.status === 'in_progress').length} Live
            </span>
          </div>

          {/* Scrolling Match Feed Items */}
          <div
            className="flex-1 w-full overflow-x-auto no-scrollbar flex items-center space-x-3 py-0.5 cursor-pointer"
            onMouseEnter={() => setIsTickerPaused(true)}
            onMouseLeave={() => setIsTickerPaused(false)}
          >
            {allLiveMatches.map(({ match, tournament }) => {
              const isInProgress = match.status === 'in_progress';
              const isCompleted = match.status === 'completed';

              return (
                <div
                  key={`ticker_${tournament.id}_${match.id}`}
                  onClick={() => {
                    setSelectedTournamentId(tournament.id);
                    setViewTab('current');
                    triggerToast(`Jumping to ${tournament.title} • Match #${match.matchNumber}! 🏀`);
                  }}
                  className={`px-3 py-1.5 rounded-xl border transition shrink-0 flex items-center space-x-2 text-xs font-mono group hover:scale-[1.02] ${
                    isInProgress
                      ? 'bg-gradient-to-r from-rose-950/80 to-indigo-950 border-rose-500/60 text-white shadow-md shadow-rose-500/10'
                      : isCompleted
                      ? 'bg-indigo-950/90 border-amber-400/40 text-amber-200'
                      : 'bg-indigo-950/60 border-white/10 text-indigo-200'
                  }`}
                >
                  {/* Status Tag */}
                  {isInProgress ? (
                    <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black uppercase rounded animate-pulse">
                      LIVE
                    </span>
                  ) : isCompleted ? (
                    <span className="px-1.5 py-0.5 bg-amber-400 text-black text-[9px] font-black uppercase rounded">
                      FINAL
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-indigo-800 text-indigo-200 text-[9px] font-bold uppercase rounded">
                      {match.scheduledTime || 'UPCOMING'}
                    </span>
                  )}

                  {/* Sport Icon */}
                  <span className="text-sm">
                    {tournament.sport === 'basketball' ? '🏀' : tournament.sport === 'pickleball' ? '🏓' : tournament.sport === 'soccer' ? '⚽' : '⚾'}
                  </span>

                  {/* Teams & Score */}
                  <div className="flex items-center space-x-1.5 font-bold">
                    <span className="text-white group-hover:text-lime-300 transition">{match.team1?.name || 'TBD'}</span>
                    <span className="px-1.5 py-0.5 bg-black/40 rounded text-lime-400 font-extrabold text-[11px]">
                      {match.score1} - {match.score2}
                    </span>
                    <span className="text-white group-hover:text-lime-300 transition">{match.team2?.name || 'TBD'}</span>
                  </div>

                  {/* Round Info */}
                  <span className="text-[10px] text-indigo-300/80 hidden lg:inline font-sans font-semibold">
                    ({match.roundName})
                  </span>
                </div>
              );
            })}
          </div>

          {/* Pause / Resume Indicator */}
          <div className="flex items-center space-x-1 text-[10px] text-indigo-300 shrink-0">
            <button
              type="button"
              onClick={() => setIsTickerPaused(!isTickerPaused)}
              className="p-1.5 rounded-lg bg-indigo-900/60 hover:bg-white/10 transition text-indigo-200 hover:text-white"
              title={isTickerPaused ? 'Resume Ticker' : 'Pause Ticker'}
            >
              {isTickerPaused ? <Play className="w-3.5 h-3.5 text-lime-400" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}

      {/* Primary Navigation Mode Sub-Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-indigo-950/80 p-1.5 rounded-2xl border border-white/10">
        <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto">
          <button
            onClick={() => setViewTab('current')}
            className={`px-3.5 py-2.5 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 whitespace-nowrap ${
              viewTab === 'current'
                ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <Trophy className="w-4 h-4" />
            <span>Active & Upcoming Brackets</span>
          </button>

          <button
            onClick={() => setViewTab('calendar')}
            className={`px-3.5 py-2.5 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 whitespace-nowrap ${
              viewTab === 'calendar'
                ? 'bg-cyan-400 text-black shadow-lg shadow-cyan-400/20'
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            <span>Tournament Calendar</span>
            <span className="px-1.5 py-0.5 bg-black/20 text-black text-[9px] rounded-full font-extrabold">
              {tournaments.filter((t) => t.status !== 'completed').length}
            </span>
          </button>

          <button
            onClick={() => setViewTab('history')}
            className={`px-3.5 py-2.5 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 whitespace-nowrap ${
              viewTab === 'history'
                ? 'bg-amber-400 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <History className="w-4 h-4" />
            <span>Tournament History & Hall of Fame</span>
            <span className="px-1.5 py-0.5 bg-black/20 text-black text-[9px] rounded-full font-extrabold">
              {tournaments.filter((t) => t.status === 'completed').length}
            </span>
          </button>

          <button
            onClick={() => setViewTab('leaderboard')}
            className={`px-3.5 py-2.5 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 whitespace-nowrap ${
              viewTab === 'leaderboard'
                ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black shadow-lg shadow-amber-400/20'
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            }`}
          >
            <Crown className="w-4 h-4 text-amber-400" />
            <span>Tournament Leaderboard 🏆</span>
          </button>
        </div>

        {/* Live Feed Ticker Trigger Button */}
        <button
          onClick={() => setIsLiveDrawerOpen(true)}
          className="px-3.5 py-2.5 rounded-xl text-xs font-black uppercase italic transition flex items-center space-x-2 bg-gradient-to-r from-rose-500/20 via-rose-500/30 to-indigo-900 border border-rose-500/50 text-rose-300 hover:text-white hover:border-rose-400 shadow-md shadow-rose-500/10 shrink-0"
        >
          <Radio className="w-4 h-4 text-rose-400 animate-pulse" />
          <span>Live Feed</span>
          <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] rounded-full font-black animate-pulse">
            LIVE
          </span>
        </button>
      </div>
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-indigo-300/60 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search tournament name, court, or host..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-indigo-950/80 border border-white/10 rounded-2xl text-xs font-bold text-white placeholder-indigo-300/40 outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          {/* Sport Filter Pills */}
          <div className="flex items-center space-x-1 overflow-x-auto pb-1 sm:pb-0">
            {[
              { id: 'all', label: 'All Sports' },
              { id: 'volleyball', label: 'Volleyball 🏐' },
              { id: 'basketball', label: 'Basketball 🏀' },
              { id: 'pickleball', label: 'Pickleball 🏓' },
              { id: 'soccer', label: 'Soccer ⚽' },
              { id: 'baseball', label: 'Baseball ⚾' },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedSport(s.id as any)}
                className={`px-3 py-2 rounded-xl text-xs font-extrabold whitespace-nowrap transition ${
                  selectedSport === s.id
                    ? 'bg-lime-400 text-black font-black'
                    : 'bg-indigo-950/80 text-indigo-200/70 hover:text-white border border-white/5'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex items-center space-x-2 border-b border-white/10 pb-2 overflow-x-auto text-xs font-black uppercase italic">
          {[
            { id: 'all', label: 'All Events' },
            { id: 'in_progress', label: '⚡ Live In Progress' },
            { id: 'registration_open', label: '📝 Registration Open' },
            { id: 'upcoming', label: '📅 Upcoming' },
            { id: 'completed', label: '🥇 Completed' },
          ].map((st) => (
            <button
              key={st.id}
              onClick={() => setSelectedStatus(st.id as any)}
              className={`px-3 py-1.5 rounded-xl transition ${
                selectedStatus === st.id
                  ? 'bg-indigo-900 text-lime-400 border border-lime-400/40 shadow'
                  : 'text-indigo-300/60 hover:text-indigo-100'
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
      </div>

      {/* TOURNAMENT VENUES MINI-MAP COMPONENT */}
      <div className="p-5 bg-indigo-950/90 rounded-3xl border border-white/10 shadow-2xl space-y-4 relative overflow-hidden">
        {/* Map Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
          <div>
            <div className="flex items-center space-x-2 text-xs font-black uppercase text-cyan-400 tracking-wider">
              <Map className="w-4 h-4 text-cyan-400" />
              <span>Tournament Venues & Court Map</span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white mt-0.5">
              Interactive Playground Court Pin Map
            </h3>
            <p className="text-xs text-indigo-200">
              Click any court pin to view venue amenities, lighting, active players, and get instant GPS directions.
            </p>
          </div>

          {/* Map Filters & Actions */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 bg-indigo-900/80 p-1 rounded-xl border border-white/10 text-xs font-bold">
              {['all', 'basketball', 'pickleball', 'soccer', 'baseball'].map((sp) => (
                <button
                  key={`map_sp_${sp}`}
                  type="button"
                  onClick={() => setMapFilterSport(sp as any)}
                  className={`px-2.5 py-1 rounded-lg uppercase text-[10px] transition ${
                    mapFilterSport === sp
                      ? 'bg-cyan-400 text-black font-black shadow'
                      : 'text-indigo-300 hover:text-white'
                  }`}
                >
                  {sp === 'all' ? 'All Courts' : sp}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setIsMapExpanded(!isMapExpanded)}
              className="p-2 bg-indigo-900 hover:bg-indigo-800 rounded-xl text-indigo-200 hover:text-white transition border border-white/10"
              title={isMapExpanded ? 'Collapse Map' : 'Expand Map'}
            >
              {isMapExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Visual Map Canvas Container */}
        <div className={`relative w-full rounded-2xl border border-cyan-500/30 overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 shadow-inner transition-all ${
          isMapExpanded ? 'h-96' : 'h-64'
        }`}>
          {/* Styled Grid Background Lines / Radar Rings */}
          <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:16px_16px] opacity-15 pointer-events-none" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
            <div className="w-72 h-72 rounded-full border-2 border-dashed border-cyan-400 animate-spin-slow" />
            <div className="w-48 h-48 rounded-full border border-cyan-400 absolute" />
          </div>

          {/* Map Legend Badge */}
          <div className="absolute top-3 left-3 z-10 bg-black/80 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/10 text-[10px] font-mono text-cyan-300 flex items-center space-x-2 shadow">
            <Compass className="w-3.5 h-3.5 text-cyan-400" />
            <span>{filteredMapCourts.length} Playground Venues Plotted</span>
          </div>

          {/* Map Court Pins Plotter */}
          {filteredMapCourts.map((court, index) => {
            const minLat = 40.70; const maxLat = 40.85;
            const minLng = -74.02; const maxLng = -73.90;
            let xPct = ((court.lng - minLng) / (maxLng - minLng)) * 80 + 10;
            let yPct = 100 - (((court.lat - minLat) / (maxLat - minLat)) * 80 + 10);
            
            if (isNaN(xPct) || xPct < 5 || xPct > 95) xPct = 15 + (index * 22) % 75;
            if (isNaN(yPct) || yPct < 5 || yPct > 95) yPct = 20 + (index * 28) % 70;

            const isSelected = selectedMapCourtId === court.id;
            const tourneysAtCourt = tournaments.filter(t => t.courtId === court.id || t.courtName.toLowerCase().includes(court.name.toLowerCase()));
            const tourneyCount = tourneysAtCourt.length;
            const hasTourneys = tourneyCount > 0;

            return (
              <div
                key={`court_pin_${court.id}`}
                style={{ left: `${xPct}%`, top: `${yPct}%` }}
                onClick={() => setSelectedMapCourtId(court.id)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 z-20 cursor-pointer group transition-all transform hover:scale-125 ${
                  isSelected ? 'scale-125 z-30' : ''
                }`}
              >
                {/* Active Tournament Radar Glow Ring */}
                {hasTourneys && (
                  <div className="absolute -inset-2 rounded-full bg-lime-400/30 animate-ping pointer-events-none" />
                )}

                {/* Pin Marker Bubble */}
                <div className={`p-2 rounded-2xl shadow-xl border flex items-center space-x-1.5 transition ${
                  isSelected
                    ? 'bg-cyan-400 text-black border-white shadow-cyan-400/50 scale-110 font-extrabold'
                    : hasTourneys
                    ? 'bg-gradient-to-r from-lime-400 to-emerald-400 text-black border-lime-300 font-black'
                    : 'bg-indigo-900/90 text-white border-white/20 hover:bg-indigo-800'
                }`}>
                  <MapPin className={`w-4 h-4 ${isSelected ? 'text-black' : hasTourneys ? 'text-black' : 'text-cyan-400'}`} />
                  <span className="text-[11px] font-black italic whitespace-nowrap max-w-[120px] truncate">
                    {court.name.split(' ')[0]}
                  </span>
                  {hasTourneys && (
                    <span className="px-1.5 py-0.2 bg-black text-lime-400 text-[9px] font-black rounded-full">
                      {tourneyCount}🏆
                    </span>
                  )}
                </div>

                {/* Hover Tooltip Popup */}
                <div className="opacity-0 group-hover:opacity-100 transition absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2.5 bg-black/95 backdrop-blur-md text-white text-[10px] rounded-xl border border-white/20 whitespace-nowrap shadow-2xl pointer-events-none z-40 space-y-0.5">
                  <p className="font-extrabold text-lime-400">{court.name}</p>
                  <p className="text-indigo-200">{court.address}</p>
                  <p className="text-cyan-300 font-bold">⭐ {court.rating} • {court.activePlayersNow || 8} Athletes On Court</p>
                </div>
              </div>
            );
          })}

          {/* Reset View Button */}
          <div className="absolute bottom-3 right-3 z-10 flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setSelectedMapCourtId(null)}
              className="px-3 py-1.5 bg-indigo-900/90 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl text-xs font-bold border border-white/10 shadow transition flex items-center space-x-1"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Reset Map Focus</span>
            </button>
          </div>
        </div>

        {/* Selected Court Details Card Drawer */}
        <AnimatePresence>
          {selectedCourtObj && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="p-4 bg-gradient-to-r from-indigo-900 via-indigo-950 to-indigo-900 rounded-2xl border border-cyan-400/50 shadow-xl space-y-3"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400 text-black font-black flex items-center justify-center text-lg shrink-0">
                    {selectedCourtObj.sport === 'basketball' ? '🏀' : selectedCourtObj.sport === 'pickleball' ? '🏓' : selectedCourtObj.sport === 'soccer' ? '⚽' : '⚾'}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="text-base font-black italic uppercase text-white">{selectedCourtObj.name}</h4>
                      <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-[10px] font-bold rounded-full border border-cyan-400/30">
                        ⭐ {selectedCourtObj.rating} Rating
                      </span>
                    </div>
                    <p className="text-xs text-indigo-200 flex items-center space-x-1.5 mt-0.5">
                      <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                      <span>{selectedCourtObj.address}</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Get Directions Button */}
                  <button
                    type="button"
                    onClick={() => {
                      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        selectedCourtObj.name + ' ' + selectedCourtObj.address
                      )}`;
                      window.open(mapsUrl, '_blank');
                      triggerToast(`Opening Google Maps directions for ${selectedCourtObj.name}! 🗺️`);
                    }}
                    className="px-4 py-2 bg-cyan-400 hover:bg-cyan-300 text-black font-black uppercase text-xs rounded-xl shadow-lg shadow-cyan-400/20 transition flex items-center space-x-1.5"
                  >
                    <Navigation className="w-4 h-4 fill-black" />
                    <span>Get Directions</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedMapCourtId(null)}
                    className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Court Amenities Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                  <span className="text-[10px] text-indigo-300 block font-sans uppercase">Surface Quality</span>
                  <span className="font-bold text-white">{selectedCourtObj.surfaceType || 'Asphalt'}</span>
                </div>
                <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                  <span className="text-[10px] text-indigo-300 block font-sans uppercase">Night Lighting</span>
                  <span className="font-bold text-lime-400">{selectedCourtObj.lighting ? '💡 Night Lights Available' : '🚫 Day Use Only'}</span>
                </div>
                <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                  <span className="text-[10px] text-indigo-300 block font-sans uppercase">Active Players</span>
                  <span className="font-bold text-cyan-300">{selectedCourtObj.activePlayersNow || 8} Athletes On Court</span>
                </div>
                <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-white/5">
                  <span className="text-[10px] text-indigo-300 block font-sans uppercase">Court Rim/Net</span>
                  <span className="font-bold text-amber-300">{selectedCourtObj.rimCondition || 'Pro Breakaway Glass'}</span>
                </div>
              </div>

              {/* Tournaments Hosted Here */}
              <div className="pt-1 flex items-center justify-between text-xs">
                <span className="text-indigo-200 font-medium">
                  Tournaments hosted at venue: <strong className="text-lime-400">
                    {tournaments.filter(t => t.courtId === selectedCourtObj.id || t.courtName.toLowerCase().includes(selectedCourtObj.name.toLowerCase())).length} Events
                  </strong>
                </span>

                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery(selectedCourtObj.name.split(' ')[0]);
                    triggerToast(`Filtered tournaments for ${selectedCourtObj.name}!`);
                  }}
                  className="text-lime-400 hover:underline font-bold flex items-center space-x-1"
                >
                  <span>Filter Tournaments at Venue →</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content Area: Switch between Calendar, History & Active Brackets */}
      {viewTab === 'calendar' ? (
        /* TOURNAMENT CALENDAR VIEW */
        <div className="space-y-6">
          {/* Calendar Header Banner */}
          <div className="p-6 bg-gradient-to-r from-indigo-950 via-cyan-950 to-indigo-950 rounded-3xl border border-cyan-400/30 shadow-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 text-cyan-400 text-xs font-black uppercase tracking-widest mb-1">
                <CalendarDays className="w-4 h-4 text-cyan-400" />
                <span>Primary Sport Tournament Calendar</span>
              </div>
              <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tight text-white">
                Upcoming Schedule & Registration Deadlines
              </h2>
              <p className="text-xs text-indigo-200 mt-1 font-medium max-w-xl">
                Stay updated on upcoming tip-off dates, court venues, entry deadlines, and prize pool rewards customized for your primary sport.
              </p>
            </div>

            {/* Primary Sport Focus Badge */}
            <div className="p-3.5 bg-cyan-900/60 border border-cyan-400/40 rounded-2xl flex items-center space-x-3 shrink-0">
              <div className="w-10 h-10 rounded-xl bg-cyan-400 text-black font-black flex items-center justify-center text-lg">
                {user?.primarySport === 'basketball' ? '🏀' : user?.primarySport === 'pickleball' ? '🏓' : user?.primarySport === 'soccer' ? '⚽' : user?.primarySport === 'baseball' ? '⚾' : '🏆'}
              </div>
              <div>
                <span className="text-[10px] text-cyan-300 font-bold block uppercase">Primary Sport Focus</span>
                <span className="text-sm font-black text-white capitalize">
                  {user?.primarySport || 'Basketball'} Calendar
                </span>
              </div>
            </div>
          </div>

          {/* Key Deadline Tickers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-indigo-950/90 rounded-2xl border border-white/10 space-y-1">
              <div className="flex items-center space-x-2 text-lime-400 text-xs font-bold uppercase">
                <Clock className="w-4 h-4" />
                <span>Next Scheduled Event</span>
              </div>
              <p className="text-sm font-extrabold text-white truncate">
                {calendarTournaments[0]?.title || 'Sunset Park Championship'}
              </p>
              <p className="text-xs text-indigo-300 font-mono">
                📅 Tip-off: {calendarTournaments[0]?.startDate || 'August 25, 2026'}
              </p>
            </div>

            <div className="p-4 bg-indigo-950/90 rounded-2xl border border-rose-500/30 space-y-1">
              <div className="flex items-center space-x-2 text-rose-400 text-xs font-bold uppercase">
                <Flame className="w-4 h-4 text-rose-400 animate-pulse" />
                <span>Urgent Registration Closing</span>
              </div>
              <p className="text-sm font-extrabold text-white truncate">
                {calendarTournaments[0]?.title || 'Summer Slam Cup'}
              </p>
              <p className="text-xs text-rose-300 font-mono font-bold">
                ⏰ Deadline in 3 Days!
              </p>
            </div>

            <div className="p-4 bg-indigo-950/90 rounded-2xl border border-amber-400/30 space-y-1">
              <div className="flex items-center space-x-2 text-amber-400 text-xs font-bold uppercase">
                <Coins className="w-4 h-4 text-amber-400" />
                <span>Active Prize Pools Ahead</span>
              </div>
              <p className="text-sm font-extrabold text-white">
                $3,500 + Trophies
              </p>
              <p className="text-xs text-amber-200/80">
                Across {calendarTournaments.length} Scheduled Tournaments
              </p>
            </div>
          </div>

          {/* Calendar Agenda List */}
          <div className="space-y-4">
            <h3 className="text-sm font-black italic uppercase text-indigo-200 flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-lime-400" />
              <span>Scheduled Upcoming Tournaments ({calendarTournaments.length})</span>
            </h3>

            {calendarTournaments.length > 0 ? (
              <div className="space-y-4">
                {calendarTournaments.map((t) => {
                  const isClosingSoon = t.status === 'registration_open';
                  const isUserPrimary = user?.primarySport ? t.sport === user.primarySport : t.sport === 'basketball';

                  return (
                    <motion.div
                      key={t.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-5 bg-indigo-950/90 rounded-3xl border transition space-y-4 shadow-xl relative overflow-hidden ${
                        isUserPrimary
                          ? 'border-lime-400/60 shadow-lime-400/10'
                          : 'border-white/10 hover:border-lime-400/40'
                      }`}
                    >
                      {/* Top Sport & Primary Tag */}
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
                        <div className="flex items-center space-x-2">
                          <span className="px-2.5 py-0.5 bg-indigo-900 text-lime-300 text-[10px] font-black uppercase rounded-full border border-lime-400/30">
                            {t.sport.toUpperCase()}
                          </span>
                          {isUserPrimary && (
                            <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[9px] font-black uppercase rounded-full italic shadow">
                              ⭐ Your Primary Sport
                            </span>
                          )}
                          <span className="text-xs text-indigo-300 font-mono font-bold">
                            Format: {t.format.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>

                        {/* Status / Countdown Badge */}
                        <div className="flex items-center space-x-2">
                          {isClosingSoon ? (
                            <span className="px-3 py-1 bg-rose-500/20 text-rose-300 border border-rose-500/40 rounded-xl text-xs font-mono font-bold flex items-center space-x-1 animate-pulse">
                              <Clock className="w-3.5 h-3.5 text-rose-400" />
                              <span>Registration Closes Soon!</span>
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 rounded-xl text-xs font-mono font-bold">
                              📅 Tip-off {t.startDate}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Main Info Row */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Date Calendar Box */}
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-900 to-indigo-950 border border-lime-400/50 flex flex-col items-center justify-center text-center shadow-lg shrink-0">
                            <span className="text-[10px] font-black uppercase text-lime-400">
                              {new Date(t.startDate).toLocaleDateString([], { month: 'short' })}
                            </span>
                            <span className="text-xl font-black text-white font-mono leading-none mt-0.5">
                              {new Date(t.startDate).getDate() || 25}
                            </span>
                          </div>

                          <div>
                            <h4 className="text-lg font-black italic uppercase text-white">{t.title}</h4>
                            <p className="text-xs text-indigo-200 mt-0.5 flex items-center space-x-2">
                              <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                              <span>{t.courtName} ({t.address})</span>
                            </p>
                          </div>
                        </div>

                        {/* Capacity & Prize Details */}
                        <div className="flex items-center space-x-4 text-xs font-mono">
                          <div className="p-2.5 bg-indigo-900/60 rounded-xl border border-white/5">
                            <span className="text-[10px] text-indigo-300 block uppercase font-sans">Prize Reward</span>
                            <span className="font-bold text-amber-400">{t.prizePool || '$500 Gift Cards'}</span>
                          </div>

                          <div className="p-2.5 bg-indigo-900/60 rounded-xl border border-white/5">
                            <span className="text-[10px] text-indigo-300 block uppercase font-sans">Cap Capacity</span>
                            <span className="font-bold text-cyan-300">{t.registeredTeams.length} / {t.maxTeams} Teams</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/10 text-xs">
                        <div className="text-indigo-300 text-[11px] font-medium flex items-center space-x-2">
                          <span>Organizer: <strong className="text-white">{t.organizerName}</strong></span>
                          <span>•</span>
                          <span>Entry: <strong className="text-lime-400">{t.entryFee || 'Free'}</strong></span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTournamentId(t.id);
                              setViewTab('current');
                            }}
                            className="px-3.5 py-2 bg-indigo-900 hover:bg-indigo-800 text-white rounded-xl font-extrabold transition border border-white/10 flex items-center space-x-1.5"
                          >
                            <Trophy className="w-3.5 h-3.5 text-lime-400" />
                            <span>View Bracket</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setShowRegisterModal(t.id)}
                            className="px-4 py-2 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black uppercase italic rounded-xl transition shadow-md shadow-lime-400/20 flex items-center space-x-1.5"
                          >
                            <Users className="w-3.5 h-3.5" />
                            <span>Register Team</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setShareModalTournament(t);
                              setSharePostText(`🏆 Tournament Registration Open: "${t.title}" at ${t.courtName}! Start Date: ${t.startDate}. Prize Pool: ${t.prizePool || 'Bragging Rights'}. Register your squad on Playground League! #PlaygroundLeague`);
                            }}
                            className="p-2 bg-indigo-900 hover:bg-white/10 text-indigo-200 hover:text-white rounded-xl transition border border-white/10"
                            title="Share to Social"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center bg-indigo-950/80 rounded-3xl border border-white/10 text-xs text-indigo-300">
                No upcoming tournaments scheduled for this sport filter. Check back soon or organize a tournament!
              </div>
            )}
          </div>
        </div>
      ) : viewTab === 'history' ? (
        /* TOURNAMENT HISTORY & HALL OF FAME SECTION */
        <div className="space-y-6">
          <div className="p-5 bg-gradient-to-r from-amber-950 via-indigo-950 to-amber-950 rounded-3xl border border-amber-400/30 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
            <div>
              <div className="flex items-center space-x-2 text-amber-400 text-xs font-black uppercase tracking-widest mb-1">
                <Crown className="w-4 h-4" />
                <span>Court Champions Showcase</span>
              </div>
              <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tight text-white">
                Tournament History & Prize Distributions
              </h2>
              <p className="text-xs text-indigo-200/80 mt-1 font-medium max-w-xl">
                Review past completed tournaments, official winning teams, automated MVP awardees, and rewards distributions across local court cups.
              </p>
            </div>

            <div className="px-4 py-2 bg-amber-400/20 border border-amber-400/40 rounded-2xl flex items-center space-x-3 shrink-0">
              <Trophy className="w-8 h-8 text-amber-400" />
              <div>
                <span className="text-[10px] text-amber-300 font-bold block uppercase">Historical Records</span>
                <span className="text-lg font-mono font-black text-white">
                  {tournaments.filter((t) => t.status === 'completed').length} Completed Cups
                </span>
              </div>
            </div>
          </div>

          {/* TEAM WIN-RATE COMPARISON CHART (RECHARTS) */}
          <div className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 shadow-2xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-4">
              <div>
                <div className="flex items-center space-x-2 text-lime-400 text-xs font-black uppercase tracking-wider">
                  <Star className="w-4 h-4 text-lime-400" />
                  <span>Historical Performance Analytics</span>
                </div>
                <h3 className="text-lg font-black italic uppercase text-white mt-1">
                  Team Win-Rate Comparison Across Past Tournaments
                </h3>
                <p className="text-xs text-indigo-200/80 font-medium">
                  Aggregated win percentages and match outcomes across completed court cups & tournament brackets
                </p>
              </div>

              {teamWinRateData.length > 0 && (
                <div className="flex items-center space-x-2 bg-indigo-900/80 px-3.5 py-1.5 rounded-xl border border-lime-400/30 text-xs font-mono font-bold text-lime-300">
                  <Crown className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Top Win Rate: {teamWinRateData[0]?.teamName} ({teamWinRateData[0]?.winRate}%)</span>
                </div>
              )}
            </div>

            {teamWinRateData.length > 0 ? (
              <div className="space-y-4">
                <div className="h-64 sm:h-72 w-full pt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={teamWinRateData} margin={{ top: 15, right: 20, left: -10, bottom: 25 }}>
                      <defs>
                        <linearGradient id="winRateGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#a3e635" stopOpacity={0.9} />
                          <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.6} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                      <XAxis
                        dataKey="teamName"
                        stroke="#94a3b8"
                        fontSize={11}
                        fontWeight="bold"
                        tickLine={false}
                        interval={0}
                        angle={-15}
                        textAnchor="end"
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        fontWeight="bold"
                        tickLine={false}
                        domain={[0, 100]}
                        tickFormatter={(val) => `${val}%`}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-indigo-950/95 border border-lime-400/50 p-3 rounded-2xl shadow-2xl text-xs space-y-1">
                                <p className="font-extrabold text-white text-sm">{data.teamName}</p>
                                <div className="text-lime-400 font-mono font-black text-base">
                                  Win Rate: {data.winRate}%
                                </div>
                                <p className="text-indigo-200 text-[11px]">
                                  Wins: <span className="font-bold text-white">{data.wins}</span> • Losses: <span className="font-bold text-rose-300">{data.losses}</span>
                                </p>
                                <p className="text-indigo-300 text-[10px] italic">
                                  Total Matches: {data.totalMatches}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="winRate" radius={[8, 8, 0, 0]} maxBarSize={55}>
                        {teamWinRateData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? 'url(#winRateGradient)' : '#38bdf8'}
                            stroke={index === 0 ? '#a3e635' : '#0284c7'}
                            strokeWidth={1.5}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Team Quick Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                  {teamWinRateData.slice(0, 4).map((t, i) => (
                    <div key={t.teamName} className="p-2.5 bg-indigo-900/40 rounded-2xl border border-white/5 space-y-1 text-xs">
                      <div className="flex items-center justify-between text-[10px] text-indigo-300/70 font-bold">
                        <span>Rank #{i + 1}</span>
                        <span className="text-lime-400 font-mono font-black">{t.winRate}% WR</span>
                      </div>
                      <p className="font-extrabold text-white truncate">{t.teamName}</p>
                      <p className="text-[10px] text-indigo-200/80 font-medium">
                        {t.wins} W - {t.losses} L ({t.totalMatches} GP)
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center bg-indigo-900/30 rounded-2xl border border-white/5 text-xs text-indigo-300">
                No past tournament match statistics recorded yet. Play completed tournament games to build the win-rate chart!
              </div>
            )}
          </div>

          {/* Historical Tournaments Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {tournaments
              .filter((t) => t.status === 'completed' || t.mvpPlayerName)
              .map((hist) => {
                const histMVP = computeTournamentMVP(hist);
                const isFinalExpanded = !!expandedMatchIds[`hist_final_${hist.id}`];

                return (
                  <div
                    key={hist.id}
                    className="p-6 bg-indigo-950/90 rounded-3xl border border-white/10 space-y-5 shadow-2xl relative overflow-hidden"
                  >
                    <div className="flex items-start justify-between border-b border-white/10 pb-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="px-2.5 py-0.5 bg-amber-400 text-black text-[9px] font-black uppercase tracking-wider rounded-full italic">
                            🏆 COMPLETED CHAMPIONSHIP
                          </span>
                          <span className="text-xs text-indigo-300 font-bold uppercase">
                            {hist.sport} • {hist.format.replace('_', ' ')}
                          </span>
                        </div>
                        <h3 className="text-xl font-black italic uppercase text-white mt-1.5 leading-snug">
                          {hist.title}
                        </h3>
                        <div className="flex items-center space-x-3 text-xs text-indigo-200/80 font-medium mt-1">
                          <span className="flex items-center space-x-1">
                            <MapPin className="w-3.5 h-3.5 text-lime-400" />
                            <span>{hist.courtName}</span>
                          </span>
                          <span>•</span>
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                            <span>{hist.startDate}</span>
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDownloadBracket(hist)}
                        className="p-2.5 bg-indigo-900 hover:bg-lime-400 hover:text-black text-lime-300 rounded-xl transition border border-white/10 flex items-center space-x-1.5 text-xs font-bold shrink-0"
                        title="Download Summary"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Summary</span>
                      </button>
                    </div>

                    {/* Winning Champions Card */}
                    <div className="p-4 bg-gradient-to-r from-amber-950/70 via-indigo-900/60 to-amber-950/80 rounded-2xl border border-amber-400/40 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-300 flex items-center space-x-1">
                          <Crown className="w-3.5 h-3.5 text-amber-400" />
                          <span>1ST PLACE CHAMPIONS</span>
                        </span>
                        <span className="text-xs font-mono font-black text-lime-400">
                          {hist.winningTeamName || hist.registeredTeams[0]?.name || 'Tournament Winners'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-3 pt-1">
                        <div className="w-12 h-12 rounded-2xl bg-amber-400 text-black flex items-center justify-center font-black text-xl shadow-lg shrink-0">
                          🏆
                        </div>
                        <div>
                          <h4 className="font-extrabold text-base text-white">
                            {hist.winningTeamName || hist.registeredTeams[0]?.name || 'Champions Squad'}
                          </h4>
                          <p className="text-xs text-amber-200/80 font-medium">
                            Captain: {hist.registeredTeams[0]?.captainName || 'Team Captain'} • {hist.registeredTeams[0]?.members.length || 4} Athletes
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Automated Tournament MVP Showcase Card */}
                    {histMVP && (
                      <div className="p-4 bg-indigo-900/80 rounded-2xl border border-amber-400/30 space-y-2 relative overflow-hidden">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1.5">
                            <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-[9px] font-black uppercase rounded-full border border-amber-400/40 flex items-center space-x-1">
                              <Award className="w-3 h-3 text-amber-400" />
                              <span>OFFICIAL MVP TOURNAMENT BADGE</span>
                            </span>
                          </div>
                          <span className="text-[10px] font-bold text-amber-200 uppercase italic">
                            Automated Stat Leader
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 text-black font-black flex items-center justify-center text-sm shadow">
                              {histMVP.playerName.charAt(0)}
                            </div>
                            <div>
                              <div className="flex items-center space-x-1.5">
                                <h5 className="font-extrabold text-sm text-white">{histMVP.playerName}</h5>
                                <span className="text-[9px] bg-amber-400 text-black px-1.5 py-0.2 rounded font-black">
                                  MVP Badge
                                </span>
                              </div>
                              <p className="text-[11px] text-indigo-300 font-medium">
                                {histMVP.teamName} • {histMVP.matchesCount} Tournament Games
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 font-mono text-xs font-bold">
                            <span className="bg-indigo-950 px-2 py-1 rounded-lg text-lime-400 border border-white/10">
                              {histMVP.totalPts} PTS
                            </span>
                            <span className="bg-indigo-950 px-2 py-1 rounded-lg text-indigo-200 border border-white/10">
                              {histMVP.totalReb} REB
                            </span>
                            <span className="bg-indigo-950 px-2 py-1 rounded-lg text-cyan-300 border border-white/10">
                              {histMVP.totalAst} AST
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Prize Pool & Reward Distribution Card */}
                    <div className="p-4 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-2 text-xs">
                      <div className="flex items-center justify-between text-indigo-300 font-bold uppercase text-[10px]">
                        <span className="flex items-center space-x-1">
                          <Coins className="w-3.5 h-3.5 text-amber-400" />
                          <span>Prize Pool & Reward Distribution</span>
                        </span>
                        <span className="text-amber-300 font-extrabold">{hist.prizePool || '$1,500 Total Purse'}</span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-1 font-medium">
                        <div className="p-2.5 bg-indigo-950 rounded-xl border border-white/5 space-y-0.5">
                          <span className="text-[9px] text-amber-300 font-black uppercase block">🥇 1st Place</span>
                          <span className="font-bold text-white text-[11px]">
                            {hist.prizeDistribution?.firstPlace || hist.prizePool || '$1,000 Cash'}
                          </span>
                        </div>

                        <div className="p-2.5 bg-indigo-950 rounded-xl border border-white/5 space-y-0.5">
                          <span className="text-[9px] text-indigo-300 font-black uppercase block">🥈 2nd Place</span>
                          <span className="font-bold text-white text-[11px]">
                            {hist.prizeDistribution?.secondPlace || '$300 Cash'}
                          </span>
                        </div>

                        <div className="p-2.5 bg-indigo-950 rounded-xl border border-amber-400/30 space-y-0.5">
                          <span className="text-[9px] text-amber-300 font-black uppercase block">🎖️ MVP Award</span>
                          <span className="font-bold text-amber-300 text-[11px]">
                            {hist.prizeDistribution?.mvpReward || '$200 Gear Voucher'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Collapsible Final Championship Game Box Score */}
                    {hist.matches.length > 0 && (
                      <div className="pt-2 border-t border-white/10">
                        <button
                          type="button"
                          onClick={(e) => toggleMatchExpanded(`hist_final_${hist.id}`, e)}
                          className="w-full py-2 bg-indigo-900/60 hover:bg-indigo-900 text-indigo-200 hover:text-white rounded-xl text-xs font-bold border border-white/10 flex items-center justify-between px-3 transition"
                        >
                          <span className="flex items-center space-x-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                            <span>Championship Game Box Score & Recap</span>
                          </span>
                          {isFinalExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        <AnimatePresence>
                          {isFinalExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-2 space-y-2 overflow-hidden"
                            >
                              <div className="bg-indigo-950 p-3 rounded-2xl border border-white/10 space-y-2 text-xs">
                                {hist.matches.slice(-1).map((m) => (
                                  <div key={m.id} className="space-y-2">
                                    <div className="flex justify-between text-[10px] text-indigo-300 font-bold uppercase">
                                      <span>Championship Final • {m.scheduledTime || '7:00 PM'}</span>
                                      <span className="text-lime-400 font-mono">FINAL SCORE</span>
                                    </div>

                                    <div className="flex justify-between items-center p-2 bg-indigo-900/60 rounded-xl font-bold">
                                      <span>{m.team1?.name || 'Team 1'}</span>
                                      <span className="font-mono text-lime-400 text-sm font-black">{m.score1 || 25}</span>
                                    </div>

                                    <div className="flex justify-between items-center p-2 bg-indigo-900/60 rounded-xl font-bold">
                                      <span>{m.team2?.name || 'Team 2'}</span>
                                      <span className="font-mono text-lime-400 text-sm font-black">{m.score2 || 19}</span>
                                    </div>

                                    {m.boxScores && m.boxScores.length > 0 && (
                                      <div className="pt-2 border-t border-white/5 space-y-1">
                                        <span className="text-[10px] text-amber-300 font-black uppercase block">Individual Player Box Scores:</span>
                                        {m.boxScores.map((bs, i) => (
                                          <div key={i} className="flex justify-between text-[11px] font-medium py-0.5">
                                            <span>{bs.playerName} ({bs.teamName})</span>
                                            <span className="font-mono text-lime-400 font-bold">{bs.pts} PTS • {bs.reb} REB • {bs.ast} AST</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      ) : viewTab === 'leaderboard' ? (
        <TournamentLeaderboardSection
          tournaments={tournaments}
          leaderboardMode={leaderboardMode}
          setLeaderboardMode={setLeaderboardMode}
          selectedLeaderboardTournament={selectedLeaderboardTournament}
          setSelectedLeaderboardTournament={setSelectedLeaderboardTournament}
          leaderboardSearch={leaderboardSearch}
          setLeaderboardSearch={setLeaderboardSearch}
          selectedSport={selectedSport}
        />
      ) : (
        <div className="space-y-6">
          {/* REAL-TIME TOURNAMENT STATUS BOARD */}
          <div className="p-5 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 rounded-3xl border border-lime-400/40 shadow-2xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2.5">
                <div className="p-2.5 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
                  <Radio className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-base font-black italic uppercase text-white tracking-tight">Real-Time Tournament Status Board</h3>
                    <span className="px-2 py-0.5 bg-rose-500 text-white rounded-full text-[9px] font-black uppercase tracking-wider animate-pulse">
                      LIVE BROADCAST
                    </span>
                  </div>
                  <p className="text-xs text-indigo-300 font-medium">
                    Instant alerts for finalized matches, prize pool updates, registration deadlines & entry fee payments
                  </p>
                </div>
              </div>

              {/* Status Board Filter Buttons */}
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 text-xs font-bold">
                {[
                  { id: 'all', label: 'All Alerts' },
                  { id: 'match', label: '⚡ Matches' },
                  { id: 'prize', label: '🏆 Prize Boosts' },
                  { id: 'deadline', label: '⏰ Deadlines' },
                  { id: 'payment', label: '💳 Entry Fees' },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setStatusBoardFilter(f.id as any)}
                    className={`px-3 py-1.5 rounded-xl whitespace-nowrap transition ${
                      statusBoardFilter === f.id
                        ? 'bg-lime-400 text-black font-black shadow-md'
                        : 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-900 hover:text-white'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Board Ticker Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {statusBoardAlerts
                .filter((a) => statusBoardFilter === 'all' || a.type === statusBoardFilter)
                .slice(0, 4)
                .map((alert) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => {
                      if (alert.tournamentId) setSelectedTournamentId(alert.tournamentId);
                      triggerToast(`Navigated to ${alert.tournamentTitle}! 🏀`);
                    }}
                    className="p-3.5 bg-indigo-900/50 hover:bg-indigo-900/80 rounded-2xl border border-white/10 hover:border-lime-400/50 transition cursor-pointer space-y-2 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className={`px-2 py-0.5 rounded-full font-black uppercase tracking-wider ${alert.badgeColor}`}>
                          {alert.title}
                        </span>
                        <span className="text-indigo-400 font-mono font-bold">{alert.timestamp}</span>
                      </div>
                      <p className="text-xs text-white font-semibold leading-snug line-clamp-2 mt-1">
                        {alert.message}
                      </p>
                    </div>
                    <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[10px] text-indigo-300">
                      <span className="truncate italic font-mono">{alert.tournamentTitle}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-lime-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </motion.div>
                ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Tournament Cards Directory (5 cols) */}
            <div className="lg:col-span-5 space-y-3">
              <div className="flex items-center justify-between text-xs font-black uppercase text-indigo-300/70 tracking-wider">
                <span>Tournaments ({filteredTournaments.length})</span>
                {filteredTournaments.length === 0 && <span className="text-rose-400">No events match filter</span>}
              </div>

              <div className="space-y-3 max-h-[750px] overflow-y-auto pr-1">
                {filteredTournaments.length === 0 ? (
                  <TournamentEmptyIllustration
                    selectedSport={selectedSport}
                    onResetFilters={() => {
                      setSearchQuery('');
                      setSelectedSport('all');
                      setSelectedStatus('all');
                    }}
                    onCreateTournament={() => setShowCreateModal(true)}
                  />
                ) : (
                  filteredTournaments.map((t) => {
                    const isSelected = activeTournament?.id === t.id;
                    const isRegOpen = t.status === 'registration_open';
                    const isFull = (t.registeredTeams || []).length >= t.maxTeams;

                    return (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTournamentId(t.id)}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer relative overflow-hidden ${
                          isSelected
                            ? 'bg-indigo-900/90 border-lime-400 shadow-xl ring-2 ring-lime-400/20'
                            : 'bg-indigo-950/70 border-white/10 hover:border-white/20 hover:bg-indigo-900/40'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase italic ${
                                t.status === 'in_progress'
                                  ? 'bg-amber-400 text-black animate-pulse'
                                  : t.status === 'registration_open'
                                  ? 'bg-lime-400 text-black'
                                  : t.status === 'completed'
                                  ? 'bg-indigo-800 text-indigo-200'
                                  : 'bg-blue-500 text-white'
                              }`}>
                                {t.status.replace('_', ' ')}
                              </span>

                              <span className="text-[10px] text-indigo-300/70 font-bold uppercase">
                                {t.format.replace('_', ' ')}
                              </span>
                            </div>

                            <h3 className="font-extrabold text-sm text-white mt-1.5 leading-snug">
                              {t.title}
                            </h3>
                          </div>

                          <span className="text-xl">
                            {t.sport === 'basketball' ? '🏀' : t.sport === 'pickleball' ? '🏓' : t.sport === 'soccer' ? '⚽' : '⚾'}
                          </span>
                        </div>

                        {/* Details summary */}
                        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-indigo-200/80 font-medium">
                          <div className="flex items-center space-x-1.5 truncate">
                            <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                            <span className="truncate">{t.courtName}</span>
                          </div>

                          <div className="flex items-center space-x-1.5">
                            <Calendar className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                            <span>{t.startDate}</span>
                          </div>

                          <div className="flex items-center space-x-1.5">
                            <Users className="w-3.5 h-3.5 text-indigo-300 shrink-0" />
                            <span className="font-bold text-white">
                              {(t.registeredTeams || []).length} / {t.maxTeams} Teams
                            </span>
                          </div>

                          <div className="flex items-center space-x-1.5">
                            <Trophy className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                            <span className="truncate text-amber-200 font-bold">{t.prizePool || 'Trophy'}</span>
                          </div>
                        </div>

                        {/* Actions Bar */}
                        <div className="mt-3.5 pt-2.5 border-t border-white/10 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTournamentId(t.id);
                            }}
                            className="text-[11px] font-black italic uppercase text-lime-400 hover:underline flex items-center space-x-1"
                          >
                            <span>View Bracket & Hub</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>

                          {isRegOpen && !isFull && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowRegisterModal(t.id);
                              }}
                              className="px-2.5 py-1 bg-rose-500 hover:bg-rose-600 text-white font-black text-[10px] uppercase rounded-xl transition shadow-sm"
                            >
                              Register Team
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

          {/* Right Column: Interactive Bracket & Tournament Hub (7 cols) */}
          <div className="lg:col-span-7 space-y-4">
            {activeTournament ? (
              <div className="bg-indigo-950/90 rounded-3xl border border-white/10 p-5 space-y-6 shadow-2xl">
                {/* Header Info */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-white/10">
                  <div>
                    <div className="flex items-center space-x-2 text-xs font-black uppercase text-lime-400 tracking-wider">
                      <Award className="w-4 h-4" />
                      <span>Tournament Overview</span>
                    </div>
                    <h2 className="text-xl font-black italic uppercase tracking-tight text-white mt-1">
                      {activeTournament.title}
                    </h2>
                    <p className="text-xs text-indigo-200/80 font-medium flex items-center space-x-2 mt-1">
                      <MapPin className="w-3.5 h-3.5 text-lime-400" />
                      <span>{activeTournament.courtName} • {activeTournament.address}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleDownloadBracket(activeTournament)}
                      className="px-3.5 py-2 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5 shrink-0"
                    >
                      <Download className="w-4 h-4 stroke-[2.5]" />
                      <span>Download Bracket</span>
                    </button>

                    {onSelectCourtOnMap && (
                      <button
                        onClick={() => onSelectCourtOnMap(activeTournament.courtId)}
                        className="px-3 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-100 rounded-xl text-xs font-bold border border-white/10 transition"
                      >
                        View Court Map
                      </button>
                    )}

                    {activeTournament.status === 'registration_open' &&
                      activeTournament.registeredTeams.length < activeTournament.maxTeams && (
                        <button
                          onClick={() => setShowRegisterModal(activeTournament.id)}
                          className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white font-black italic text-xs uppercase rounded-xl shadow-lg shadow-rose-500/20 transition"
                        >
                          + Join Tournament
                        </button>
                      )}
                  </div>
                </div>

                {/* AUTOMATED TOURNAMENT MVP BADGE BANNER CARD */}
                {activeMVP && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-gradient-to-r from-amber-950/80 via-indigo-950 to-amber-950/90 rounded-2xl border border-amber-400/50 shadow-xl relative overflow-hidden space-y-2"
                  >
                    <div className="absolute top-0 right-0 p-3 opacity-15 pointer-events-none">
                      <Crown className="w-28 h-28 text-amber-300" />
                    </div>

                    <div className="flex items-center justify-between relative z-10">
                      <div className="flex items-center space-x-2">
                        <span className="px-2.5 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-black text-[10px] uppercase tracking-wider rounded-full shadow-md flex items-center space-x-1">
                          <Award className="w-3.5 h-3.5 text-black shrink-0" />
                          <span>MVP TOURNAMENT BADGE</span>
                        </span>
                        <span className="text-[10px] font-bold text-amber-300/80 uppercase">Automated Performance Leader</span>
                      </div>

                      <span className="text-[11px] font-mono font-black text-amber-300 bg-amber-400/20 px-2 py-0.5 rounded-lg border border-amber-400/30">
                        Top Aggregate Scorer 🏆
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative z-10 pt-1">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 p-0.5 shadow-lg shrink-0">
                          <div className="w-full h-full bg-indigo-950 rounded-[14px] flex items-center justify-center font-black text-amber-300 text-lg">
                            {activeMVP.playerName.charAt(0)}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="font-extrabold text-base text-white">{activeMVP.playerName}</h4>
                            <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-[9px] font-extrabold rounded-full border border-amber-400/40">
                              🏅 MVP Badge Awarded
                            </span>
                          </div>
                          <p className="text-xs text-amber-200/80 font-medium">
                            {activeMVP.teamName} • {activeMVP.matchesCount} Tournament Matches Played
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 bg-indigo-900/80 p-2.5 rounded-xl border border-white/10 text-xs">
                        <div className="text-center px-1">
                          <span className="text-[9px] text-amber-300/70 font-black uppercase block">PTS</span>
                          <span className="font-mono font-extrabold text-lime-400 text-sm">{activeMVP.totalPts}</span>
                        </div>
                        <div className="w-px h-6 bg-white/10" />
                        <div className="text-center px-1">
                          <span className="text-[9px] text-amber-300/70 font-black uppercase block">REB</span>
                          <span className="font-mono font-extrabold text-white text-sm">{activeMVP.totalReb}</span>
                        </div>
                        <div className="w-px h-6 bg-white/10" />
                        <div className="text-center px-1">
                          <span className="text-[9px] text-amber-300/70 font-black uppercase block">AST</span>
                          <span className="font-mono font-extrabold text-cyan-300 text-sm">{activeMVP.totalAst}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Tournament Specs Strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="p-3 bg-indigo-900/50 rounded-2xl border border-white/5 space-y-0.5">
                    <span className="text-[9px] text-indigo-300/60 font-black uppercase block">Format</span>
                    <span className="font-bold text-white capitalize">{activeTournament.format.replace('_', ' ')}</span>
                  </div>

                  <div className="p-3 bg-indigo-900/50 rounded-2xl border border-white/5 space-y-0.5 relative group">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] text-indigo-300/60 font-black uppercase block">Prize Pool</span>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPrizePoolTournament(activeTournament);
                          setEditPrizePoolInput(activeTournament.prizePool || '$1,000 Cash');
                        }}
                        className="text-[10px] text-amber-300 hover:text-white underline font-bold"
                      >
                        Edit Reward
                      </button>
                    </div>
                    <div className="font-extrabold text-amber-300 flex items-center space-x-1">
                      <Coins className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <span className="truncate">{activeTournament.prizePool || 'Glory'}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-indigo-900/50 rounded-2xl border border-white/5 space-y-0.5">
                    <span className="text-[9px] text-indigo-300/60 font-black uppercase block">Entry Fee</span>
                    <span className="font-bold text-lime-400">{activeTournament.entryFee || 'Free'}</span>
                  </div>

                  <div className="p-3 bg-indigo-900/50 rounded-2xl border border-white/5 space-y-0.5">
                    <span className="text-[9px] text-indigo-300/60 font-black uppercase block">Organizer</span>
                    <span className="font-bold text-white truncate block">{activeTournament.organizerName}</span>
                  </div>
                </div>

                {/* Prize Distribution Breakdown Strip */}
                <div className="p-3.5 bg-indigo-900/40 rounded-2xl border border-amber-400/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-black uppercase text-amber-300 flex items-center space-x-1">
                      <Trophy className="w-3.5 h-3.5 text-amber-400" />
                      <span>Prize Distribution Breakdown</span>
                    </span>
                    <span className="text-[9px] text-indigo-300 font-mono">Defined by Tournament Organizer</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                    <div className="p-2 bg-indigo-950/80 rounded-xl border border-amber-400/30 space-y-0.5">
                      <span className="text-[9px] text-amber-300 font-black uppercase block">🥇 1st Place</span>
                      <span className="font-bold text-white truncate block">
                        {activeTournament.prizeDistribution?.firstPlace || activeTournament.prizePool || '$500 Cash'}
                      </span>
                    </div>

                    <div className="p-2 bg-indigo-950/80 rounded-xl border border-slate-400/30 space-y-0.5">
                      <span className="text-[9px] text-slate-300 font-black uppercase block">🥈 2nd Place</span>
                      <span className="font-bold text-white truncate block">
                        {activeTournament.prizeDistribution?.secondPlace || '$250 Cash'}
                      </span>
                    </div>

                    <div className="p-2 bg-indigo-950/80 rounded-xl border border-amber-500/30 space-y-0.5">
                      <span className="text-[9px] text-amber-400 font-black uppercase block">🥉 3rd Place</span>
                      <span className="font-bold text-white truncate block">
                        {activeTournament.prizeDistribution?.thirdPlace || '$150 Gear Voucher'}
                      </span>
                    </div>

                    <div className="p-2 bg-indigo-950/80 rounded-xl border border-lime-400/30 space-y-0.5">
                      <span className="text-[9px] text-lime-300 font-black uppercase block">🏆 MVP Award</span>
                      <span className="font-bold text-white truncate block">
                        {activeTournament.prizeDistribution?.mvpReward || '$100 Credit'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* TOURNAMENT HUB SUB-TABS NAVIGATION */}
                <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3 font-bold text-xs">
                  <button
                    type="button"
                    onClick={() => setHubSubTab('bracket')}
                    className={`px-4 py-2 rounded-xl transition flex items-center space-x-1.5 ${
                      hubSubTab === 'bracket'
                        ? 'bg-lime-400 text-black font-black shadow-lg shadow-lime-400/20'
                        : 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-900 hover:text-white'
                    }`}
                  >
                    <Swords className="w-4 h-4" />
                    <span>Match Bracket & Schedule</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setHubSubTab('radar')}
                    className={`px-4 py-2 rounded-xl transition flex items-center space-x-1.5 ${
                      hubSubTab === 'radar'
                        ? 'bg-lime-400 text-black font-black shadow-lg shadow-lime-400/20'
                        : 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-900 hover:text-white'
                    }`}
                  >
                    <BarChart2 className="w-4 h-4" />
                    <span>Team Performance Radar</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setHubSubTab('roster')}
                    className={`px-4 py-2 rounded-xl transition flex items-center space-x-1.5 ${
                      hubSubTab === 'roster'
                        ? 'bg-lime-400 text-black font-black shadow-lg shadow-lime-400/20'
                        : 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-900 hover:text-white'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    <span>Rosters & Entry Fees ({activeTournament.registeredTeams.length})</span>
                  </button>
                </div>

                {/* BRACKET SUBTAB CONTENT */}
                {hubSubTab === 'bracket' && (
                  <VisualTournamentBracket
                    tournament={activeTournament}
                    onUpdateMatchScore={onUpdateMatchScore}
                    onSetEditingMatch={setEditingMatch}
                    hoveredMatchId={hoveredMatchId}
                    setHoveredMatchId={setHoveredMatchId}
                    toggleMatchExpanded={toggleMatchExpanded}
                    expandedMatchIds={expandedMatchIds}
                  />
                )}

                {/* TEAM PERFORMANCE RADAR SUBTAB CONTENT */}
                {hubSubTab === 'radar' && (
                  <div className="space-y-5">
                    <div className="p-4 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
                        <div>
                          <div className="flex items-center space-x-2 text-xs font-black uppercase text-lime-400">
                            <BarChart2 className="w-4 h-4" />
                            <span>Aggregate Team Performance Radar</span>
                          </div>
                          <h3 className="text-base font-black italic uppercase text-white mt-0.5">
                            Offensive & Defensive Skill Matrix
                          </h3>
                          <p className="text-xs text-indigo-300">
                            Comparing Points, Rebounds, Assists, Win Rate & Efficiency
                          </p>
                        </div>

                        {/* Head-to-Head Comparison Selector */}
                        {activeTournament.registeredTeams.length >= 2 && (
                          <div className="flex items-center space-x-2 text-xs">
                            <select
                              value={compareTeam1Id || activeTournament.registeredTeams[0]?.id}
                              onChange={(e) => setCompareTeam1Id(e.target.value)}
                              className="px-2.5 py-1.5 bg-indigo-950 border border-lime-400/40 rounded-xl font-bold text-lime-300 outline-none"
                            >
                              {activeTournament.registeredTeams.map((tm) => (
                                <option key={tm.id} value={tm.id}>{tm.name}</option>
                              ))}
                            </select>

                            <span className="font-mono text-indigo-400 font-bold">VS</span>

                            <select
                              value={compareTeam2Id || activeTournament.registeredTeams[1]?.id}
                              onChange={(e) => setCompareTeam2Id(e.target.value)}
                              className="px-2.5 py-1.5 bg-indigo-950 border border-cyan-400/40 rounded-xl font-bold text-cyan-300 outline-none"
                            >
                              {activeTournament.registeredTeams.map((tm) => (
                                <option key={tm.id} value={tm.id}>{tm.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Head-to-Head Overlaid Radar Chart */}
                      {activeTournament.registeredTeams.length > 0 ? (
                        <div className="space-y-4">
                          {(() => {
                            const team1 = activeTournament.registeredTeams.find((tm) => tm.id === (compareTeam1Id || activeTournament.registeredTeams[0]?.id)) || activeTournament.registeredTeams[0];
                            const team2 = activeTournament.registeredTeams.find((tm) => tm.id === (compareTeam2Id || activeTournament.registeredTeams[1]?.id)) || activeTournament.registeredTeams[1];
                            const t1Stats = team1 ? computeTeamRadarStats(team1, activeTournament) : null;
                            const t2Stats = team2 ? computeTeamRadarStats(team2, activeTournament) : null;

                            if (!t1Stats) return null;

                            const comparisonData = t1Stats.radarData.map((item, idx) => ({
                              subject: item.metric,
                              Team1: item.value,
                              Team2: t2Stats ? t2Stats.radarData[idx]?.value || 0 : 0,
                              fullMark: 100,
                            }));

                            return (
                              <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-3 shadow-inner">
                                <div className="flex items-center justify-between text-xs font-mono">
                                  <div className="flex items-center space-x-2">
                                    <span className="w-3 h-3 rounded-full bg-lime-400 inline-block" />
                                    <span className="font-bold text-lime-300">{t1Stats.teamName} (Seed #{team1.seed || 1})</span>
                                  </div>

                                  {t2Stats && (
                                    <div className="flex items-center space-x-2">
                                      <span className="w-3 h-3 rounded-full bg-cyan-400 inline-block" />
                                      <span className="font-bold text-cyan-300">{t2Stats.teamName} (Seed #{team2.seed || 2})</span>
                                    </div>
                                  )}
                                </div>

                                <div className="h-64 w-full flex items-center justify-center">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart cx="50%" cy="50%" outerRadius="75%" data={comparisonData}>
                                      <PolarGrid stroke="#4338ca" />
                                      <PolarAngleAxis dataKey="subject" stroke="#a5b4fc" tick={{ fill: '#c7d2fe', fontSize: 11 }} />
                                      <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#6366f1" />
                                      <Radar name={t1Stats.teamName} dataKey="Team1" stroke="#a3e635" fill="#a3e635" fillOpacity={0.4} />
                                      {t2Stats && (
                                        <Radar name={t2Stats.teamName} dataKey="Team2" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.3} />
                                      )}
                                      <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
                                    </RadarChart>
                                  </ResponsiveContainer>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Grid of Team Radar Performance Breakdown Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                            {activeTournament.registeredTeams.map((team) => {
                              const radarStats = computeTeamRadarStats(team, activeTournament);
                              const isPaid = team.paymentStatus === 'Paid';

                              return (
                                <div
                                  key={team.id}
                                  className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-3 shadow"
                                >
                                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-black text-sm text-white">{team.name}</span>
                                        <span className="px-1.5 py-0.2 bg-lime-400/20 text-lime-300 text-[9px] font-mono font-bold rounded">
                                          Seed #{team.seed || 1}
                                        </span>
                                      </div>
                                      <span className="text-[10px] text-indigo-300">Captain: {team.captainName}</span>
                                    </div>

                                    {isPaid ? (
                                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full text-[10px] font-black uppercase flex items-center space-x-1">
                                        <Check className="w-3 h-3 text-emerald-400" />
                                        <span>Paid</span>
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setPayingTeamData({ tournament: activeTournament, team })}
                                        className="px-2.5 py-1 bg-amber-400 hover:bg-amber-300 text-black rounded-xl text-[10px] font-black uppercase shadow transition flex items-center space-x-1"
                                      >
                                        <CreditCard className="w-3 h-3 text-black" />
                                        <span>Pay {activeTournament.entryFee || '$20'}</span>
                                      </button>
                                    )}
                                  </div>

                                  {/* Mini Stats Summary Pill Row */}
                                  <div className="grid grid-cols-4 gap-1.5 text-[10px] font-mono text-center">
                                    <div className="p-1.5 bg-indigo-900/60 rounded-xl border border-white/5">
                                      <span className="text-[8px] text-indigo-300 block uppercase font-sans">PPG</span>
                                      <span className="font-extrabold text-lime-400">{radarStats.ppg}</span>
                                    </div>
                                    <div className="p-1.5 bg-indigo-900/60 rounded-xl border border-white/5">
                                      <span className="text-[8px] text-indigo-300 block uppercase font-sans">RPG</span>
                                      <span className="font-extrabold text-white">{radarStats.rpg}</span>
                                    </div>
                                    <div className="p-1.5 bg-indigo-900/60 rounded-xl border border-white/5">
                                      <span className="text-[8px] text-indigo-300 block uppercase font-sans">APG</span>
                                      <span className="font-extrabold text-cyan-300">{radarStats.apg}</span>
                                    </div>
                                    <div className="p-1.5 bg-indigo-900/60 rounded-xl border border-white/5">
                                      <span className="text-[8px] text-indigo-300 block uppercase font-sans">WIN%</span>
                                      <span className="font-extrabold text-amber-300">{radarStats.winRate}%</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-indigo-300 italic p-4 text-center">No teams registered yet.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* ROSTER & PAYMENTS SUBTAB CONTENT */}
                {hubSubTab === 'roster' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs font-black uppercase text-indigo-300">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-lime-400" />
                        <span>Registered Team Rosters & Entry Fee Payment Tracking ({activeTournament.registeredTeams.length} / {activeTournament.maxTeams})</span>
                      </div>

                      <span className="text-amber-300 font-mono text-[11px]">
                        Entry Fee: <strong>{activeTournament.entryFee || '$20.00'}</strong>
                      </span>
                    </div>

                    {activeTournament.registeredTeams.length > 0 ? (
                      <div className="space-y-3">
                        {activeTournament.registeredTeams.map((team, idx) => {
                          const isPaid = team.paymentStatus === 'Paid';

                          return (
                            <div
                              key={team.id || idx}
                              className="p-4 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-3 shadow-md"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2">
                                <div className="flex items-center space-x-2.5">
                                  <span className="w-7 h-7 rounded-xl bg-lime-400 text-black font-black text-xs flex items-center justify-center font-mono shadow">
                                    #{team.seed || idx + 1}
                                  </span>
                                  <div>
                                    <h4 className="font-extrabold text-sm text-white">{team.name}</h4>
                                    <span className="text-xs text-indigo-300">Captain: {team.captainName}</span>
                                  </div>
                                </div>

                                {/* Entry Fee Action */}
                                <div className="flex items-center space-x-2">
                                  {isPaid ? (
                                    <div className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/40 rounded-xl text-emerald-300 text-xs font-bold flex items-center space-x-1.5">
                                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                                      <span>Entry Fee Paid ({team.paymentReceiptId || 'REC-8910'})</span>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setPayingTeamData({ tournament: activeTournament, team })}
                                      className="px-4 py-1.5 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
                                    >
                                      <CreditCard className="w-3.5 h-3.5" />
                                      <span>Pay Entry Fee ({activeTournament.entryFee || '$20'})</span>
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Squad Members */}
                              <div className="space-y-1">
                                <span className="text-[10px] text-indigo-300 font-bold uppercase block">Squad Members ({team.members.length}):</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {team.members.map((mem, mIdx) => (
                                    <span
                                      key={mIdx}
                                      className="px-2.5 py-1 bg-indigo-950 text-indigo-200 rounded-lg text-xs font-medium border border-white/5"
                                    >
                                      👤 {typeof mem === 'string' ? mem : (mem as any)?.name || 'Squad Member'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center bg-indigo-900/20 rounded-2xl border border-white/5 text-xs text-indigo-300">
                        No teams registered yet. Be the first squad to register!
                      </div>
                    )}
                  </div>
                )}

              {/* Rules & Description */}
              <div className="p-4 bg-indigo-900/30 rounded-2xl border border-white/5 space-y-2 text-xs">
                <h4 className="font-black uppercase italic text-indigo-200">Rules & Format Guidelines</h4>
                <p className="text-indigo-200/80 font-medium leading-relaxed">
                  {activeTournament.rules || activeTournament.description}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-indigo-300/50 bg-indigo-950/50 rounded-3xl border border-white/10">
              Select a tournament on the left to view brackets and registration details.
            </div>
          )}
        </div>
      </div>
    </div>
    )}

      {/* CREATE TOURNAMENT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-white/10 rounded-3xl max-w-lg w-full p-6 space-y-4 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-lime-400" />
                <h3 className="text-lg font-black italic uppercase">Organize Court Tournament</h3>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-indigo-300" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-indigo-200 block mb-1">Tournament Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Harlem Summer 3v3 Classic"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Venue Court *</label>
                  <select
                    value={newCourtId}
                    onChange={(e) => setNewCourtId(e.target.value)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    {courts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Sport *</label>
                  <select
                    value={newSport}
                    onChange={(e) => setNewSport(e.target.value as SportType)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value="basketball">Basketball 🏀</option>
                    <option value="pickleball">Pickleball 🏓</option>
                    <option value="soccer">Soccer ⚽</option>
                    <option value="baseball">Baseball ⚾</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Tournament Format *</label>
                  <select
                    value={newFormat}
                    onChange={(e) => setNewFormat(e.target.value as TournamentFormat)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value="single_elimination">Single Elimination Bracket</option>
                    <option value="double_elimination">Double Elimination Bracket</option>
                    <option value="round_robin">Round Robin Group Stage</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Max Teams *</label>
                  <select
                    value={newMaxTeams}
                    onChange={(e) => setNewMaxTeams(Number(e.target.value))}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  >
                    <option value={4}>4 Teams Bracket</option>
                    <option value={8}>8 Teams Bracket</option>
                    <option value={16}>16 Teams Bracket</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newStartDate}
                    onChange={(e) => setNewStartDate(e.target.value)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>

                <div>
                  <label className="font-bold text-indigo-200 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={newEndDate}
                    onChange={(e) => setNewEndDate(e.target.value)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Total Prize Pool</label>
                  <input
                    type="text"
                    placeholder="e.g. $1,000 + Trophies"
                    value={newPrizePool}
                    onChange={(e) => setNewPrizePool(e.target.value)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>

                <div>
                  <label className="font-bold text-indigo-200 block mb-1">Entry Fee</label>
                  <input
                    type="text"
                    placeholder="e.g. Free or $20 / Team"
                    value={newEntryFee}
                    onChange={(e) => setNewEntryFee(e.target.value)}
                    className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
              </div>

              {/* Prize Distribution Breakdown Inputs */}
              <div className="p-3 bg-indigo-900/50 rounded-2xl border border-amber-400/30 space-y-2">
                <span className="text-[10px] font-mono font-black uppercase text-amber-300 block">
                  Prize Distribution Breakdown 🏆
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-amber-200 font-bold block mb-0.5">🥇 1st Place</label>
                    <input
                      type="text"
                      placeholder="e.g. $500 Cash"
                      value={newPrizeFirst}
                      onChange={(e) => setNewPrizeFirst(e.target.value)}
                      className="w-full p-2 bg-indigo-950 border border-white/10 rounded-lg text-white font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-200 font-bold block mb-0.5">🥈 2nd Place</label>
                    <input
                      type="text"
                      placeholder="e.g. $250 Cash"
                      value={newPrizeSecond}
                      onChange={(e) => setNewPrizeSecond(e.target.value)}
                      className="w-full p-2 bg-indigo-950 border border-white/10 rounded-lg text-white font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-amber-300 font-bold block mb-0.5">🥉 3rd Place</label>
                    <input
                      type="text"
                      placeholder="e.g. $150 Gear Voucher"
                      value={newPrizeThird}
                      onChange={(e) => setNewPrizeThird(e.target.value)}
                      className="w-full p-2 bg-indigo-950 border border-white/10 rounded-lg text-white font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-lime-300 font-bold block mb-0.5">🏆 MVP Award</label>
                    <input
                      type="text"
                      placeholder="e.g. $100 Credit"
                      value={newPrizeMvp}
                      onChange={(e) => setNewPrizeMvp(e.target.value)}
                      className="w-full p-2 bg-indigo-950 border border-white/10 rounded-lg text-white font-bold"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="font-bold text-indigo-200 block mb-1">Rules & Notes</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Games to 21, call your own fouls..."
                  value={newRules}
                  onChange={(e) => setNewRules(e.target.value)}
                  className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-xl shadow-lg transition"
                >
                  Publish Tournament
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REGISTER TEAM MODAL */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-white/10 rounded-3xl max-w-md w-full p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-lime-400" />
                <h3 className="text-lg font-black italic uppercase">Register Your Squad</h3>
              </div>
              <button
                onClick={() => setShowRegisterModal(null)}
                className="p-1 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-indigo-300" />
              </button>
            </div>

            <form onSubmit={handleRegisterSubmit} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-indigo-200 block mb-1">Team Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Brooklyn Flight"
                  value={regTeamName}
                  onChange={(e) => setRegTeamName(e.target.value)}
                  className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div>
                <label className="font-bold text-indigo-200 block mb-1">Team Captain Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Jordan Bell"
                  value={regCaptainName}
                  onChange={(e) => setRegCaptainName(e.target.value)}
                  className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div className="space-y-2 pt-1">
                <label className="font-bold text-indigo-300 block">Additional Members (Optional)</label>
                <input
                  type="text"
                  placeholder="Member #2"
                  value={regMember1}
                  onChange={(e) => setRegMember1(e.target.value)}
                  className="w-full p-2 bg-indigo-900/60 border border-white/10 rounded-xl text-white font-bold outline-none"
                />
                <input
                  type="text"
                  placeholder="Member #3"
                  value={regMember2}
                  onChange={(e) => setRegMember2(e.target.value)}
                  className="w-full p-2 bg-indigo-900/60 border border-white/10 rounded-xl text-white font-bold outline-none"
                />
              </div>

              <div className="pt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    const tId = showRegisterModal;
                    setShowRegisterModal(null);
                    setShowBulkModal(tId);
                  }}
                  className="px-3 py-2 bg-indigo-900 hover:bg-indigo-800 text-lime-300 rounded-xl font-bold text-xs transition flex items-center space-x-1.5 border border-white/10"
                >
                  <FileSpreadsheet className="w-4 h-4 text-lime-400" />
                  <span>Bulk CSV Import</span>
                </button>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowRegisterModal(null)}
                    className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl font-bold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-white font-black italic uppercase rounded-xl shadow-lg transition"
                  >
                    Submit Registration
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MATCH SCORE MODAL */}
      {editingMatch && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-indigo-950 border border-white/10 rounded-3xl max-w-sm w-full p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-sm font-black italic uppercase text-lime-400">
                  Update Match Score
                </h3>
                <span className="text-[10px] text-indigo-300 font-bold">
                  {editingMatch.match.roundName} • Match #{editingMatch.match.matchNumber}
                </span>
              </div>
              <button
                onClick={() => setEditingMatch(null)}
                className="p-1 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-indigo-300" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="space-y-2">
                <label className="font-bold text-indigo-200 block">
                  {editingMatch.match.team1?.name || 'Team 1'} Score:
                </label>
                <input
                  type="number"
                  value={editScore1}
                  onChange={(e) => setEditScore1(Number(e.target.value))}
                  className="w-full p-3 bg-indigo-900 border border-white/10 rounded-xl font-mono text-lg font-black text-lime-400 outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="font-bold text-indigo-200 block">
                  {editingMatch.match.team2?.name || 'Team 2'} Score:
                </label>
                <input
                  type="number"
                  value={editScore2}
                  onChange={(e) => setEditScore2(Number(e.target.value))}
                  className="w-full p-3 bg-indigo-900 border border-white/10 rounded-xl font-mono text-lg font-black text-lime-400 outline-none"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  onClick={() => setEditingMatch(null)}
                  className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScoreSave}
                  className="px-5 py-2 bg-lime-400 text-black font-black italic uppercase rounded-xl shadow-lg"
                >
                  Save Score
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT PRIZE POOL / REWARD MODAL */}
      {editingPrizePoolTournament && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-950 border border-white/10 rounded-3xl max-w-md w-full p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <Coins className="w-5 h-5 text-amber-400" />
                <h3 className="text-lg font-black italic uppercase">Set Prize Pool & Rewards</h3>
              </div>
              <button
                onClick={() => setEditingPrizePoolTournament(null)}
                className="p-1 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-indigo-300" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <p className="text-indigo-200/80">
                Set the cash reward or prize package value for winning teams in <strong>{editingPrizePoolTournament.title}</strong>.
              </p>

              <div>
                <label className="font-bold text-indigo-200 block mb-1">Prize Value / Description *</label>
                <input
                  type="text"
                  value={editPrizePoolInput}
                  onChange={(e) => setEditPrizePoolInput(e.target.value)}
                  placeholder="e.g. $1,500 Cash + Championship Ring"
                  className="w-full p-3 bg-indigo-900 border border-white/10 rounded-xl font-bold text-white outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>

              {/* Quick Presets */}
              <div className="space-y-1">
                <span className="text-[10px] text-indigo-300 font-bold block uppercase">Quick Presets:</span>
                <div className="flex flex-wrap gap-1.5">
                  {['$500 Cash', '$1,000 Cash', '$2,500 Prize Purse', '$5,000 Championship Purse', 'Trophy & Custom Rings'].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setEditPrizePoolInput(preset)}
                      className="px-2.5 py-1 bg-indigo-900 hover:bg-amber-400 hover:text-black text-amber-300 rounded-lg text-[10px] font-bold border border-amber-400/20 transition"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setEditingPrizePoolTournament(null)}
                  className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (onUpdatePrizePool && editPrizePoolInput.trim()) {
                      onUpdatePrizePool(editingPrizePoolTournament.id, editPrizePoolInput.trim());
                    }
                    setEditingPrizePoolTournament(null);
                  }}
                  className="px-5 py-2 bg-amber-400 hover:bg-amber-300 text-black font-black italic uppercase rounded-xl shadow-md transition"
                >
                  Save Reward
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SHARE BRACKET TO SOCIAL MODAL */}
      <AnimatePresence>
        {shareModalTournament && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-indigo-950 border border-white/20 rounded-3xl p-6 max-w-lg w-full space-y-5 shadow-2xl relative text-white"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-lime-400 text-black rounded-xl">
                    <Share2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black italic uppercase">Share Bracket to Social</h3>
                    <p className="text-xs text-indigo-300">Generate deep links or post directly to Social Feed</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShareModalTournament(null)}
                  className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Graphic Snippet Preview Card */}
              <div className="p-4 bg-gradient-to-br from-indigo-900 via-indigo-950 to-purple-950 rounded-2xl border border-lime-400/50 shadow-inner space-y-3 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[9px] font-black uppercase rounded-full tracking-wider">
                    🏆 OFFICIAL TOURNAMENT BRACKET
                  </span>
                  <span className="text-xs font-mono font-bold text-lime-300">
                    {shareModalTournament.sport.toUpperCase()}
                  </span>
                </div>

                <div>
                  <h4 className="text-xl font-black italic uppercase text-white">{shareModalTournament.title}</h4>
                  <p className="text-xs text-indigo-200 mt-0.5 flex items-center space-x-2">
                    <span>📍 {shareModalTournament.courtName}</span>
                    <span>•</span>
                    <span>📅 {shareModalTournament.startDate}</span>
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 text-xs border-t border-white/10 font-mono">
                  <div className="p-2 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-300 block uppercase">Prize Pool</span>
                    <span className="font-bold text-lime-400">{shareModalTournament.prizePool || 'Bragging Rights'}</span>
                  </div>
                  <div className="p-2 bg-indigo-950/80 rounded-xl border border-white/5">
                    <span className="text-[10px] text-indigo-300 block uppercase">Teams Registered</span>
                    <span className="font-bold text-cyan-300">{shareModalTournament.registeredTeams.length} / {shareModalTournament.maxTeams} Teams</span>
                  </div>
                </div>

                <div className="text-[10px] text-indigo-300 italic flex items-center justify-between pt-1">
                  <span>Powered by Playground League Bracket Engine</span>
                  <Sparkles className="w-3.5 h-3.5 text-lime-400" />
                </div>
              </div>

              {/* Temporary Deep Link */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-indigo-200 block uppercase">
                  Temporary Deep Link URL
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={`https://playgroundleague.app/tournament/${shareModalTournament.id}?ref=bracket_share`}
                    className="flex-1 bg-indigo-900/80 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-indigo-200 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`https://playgroundleague.app/tournament/${shareModalTournament.id}?ref=bracket_share`);
                      setShareCopied(true);
                      triggerToast('Deep link copied to clipboard! 📋');
                      setTimeout(() => setShareCopied(false), 2000);
                    }}
                    className="px-3 py-2 bg-lime-400 hover:bg-lime-300 text-black rounded-xl text-xs font-black transition flex items-center space-x-1 shrink-0"
                  >
                    {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span>{shareCopied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* Editable Post Text Area for Social Feed */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-indigo-200 block uppercase">
                  Social Feed Caption
                </label>
                <textarea
                  value={sharePostText}
                  onChange={(e) => setSharePostText(e.target.value)}
                  rows={3}
                  className="w-full bg-indigo-900/80 border border-white/10 rounded-2xl p-3 text-xs font-medium text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShareModalTournament(null)}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-indigo-300 hover:text-white bg-white/5 hover:bg-white/10"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => {
                    try {
                      const newPost = {
                        id: `bracket_post_${Date.now()}`,
                        authorId: user?.id || 'u_current',
                        authorName: user?.name || 'Marcus Vance',
                        authorAvatar: user?.avatarUrl || user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80',
                        content: sharePostText,
                        timestamp: 'Just now',
                        likesCount: 1,
                        commentsCount: 0,
                        sport: shareModalTournament.sport,
                        badgeName: 'Tournament Bracket Host 🏆',
                        isBracketPost: true,
                        tournamentId: shareModalTournament.id,
                        tournamentTitle: shareModalTournament.title,
                      };

                      const existingRaw = localStorage.getItem('playground_social_posts');
                      const existing = existingRaw ? JSON.parse(existingRaw) : [];
                      localStorage.setItem('playground_social_posts', JSON.stringify([newPost, ...existing]));
                    } catch (e) {
                      console.error('Error saving post to localStorage', e);
                    }

                    triggerToast('Tournament Bracket shared to Social Feed! 🚀');
                    setShareModalTournament(null);
                  }}
                  className="px-5 py-2.5 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black uppercase italic rounded-xl text-xs transition shadow-lg shadow-lime-400/20 flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Post to Social Feed</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ADMIN BULK TEAM REGISTRATION MODAL */}
      <AnimatePresence>
        {showBulkModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-indigo-950 border border-lime-400/50 rounded-3xl p-6 max-w-2xl w-full space-y-5 shadow-2xl relative text-white max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-lime-400 text-black rounded-xl">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black italic uppercase">Admin Bulk Team Registration</h3>
                    <p className="text-xs text-indigo-300">Import multiple teams at once via CSV upload or bulk text paste</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(null);
                    setBulkInputText('');
                    setBulkParsedTeams([]);
                  }}
                  className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mode Selector */}
              <div className="flex items-center justify-between gap-2 bg-indigo-900/60 p-1.5 rounded-2xl border border-white/10">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setBulkTab('paste')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition ${
                      bulkTab === 'paste' ? 'bg-lime-400 text-black shadow' : 'text-indigo-200 hover:text-white'
                    }`}
                  >
                    Paste CSV / Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkTab('upload')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition ${
                      bulkTab === 'upload' ? 'bg-lime-400 text-black shadow' : 'text-indigo-200 hover:text-white'
                    }`}
                  >
                    Upload CSV File
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleDownloadCsvTemplate}
                  className="px-3 py-1.5 bg-indigo-800 hover:bg-indigo-700 text-lime-300 rounded-xl text-xs font-bold transition flex items-center space-x-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Sample CSV</span>
                </button>
              </div>

              {/* Input Body */}
              {bulkTab === 'upload' ? (
                <div className="p-6 border-2 border-dashed border-lime-400/40 rounded-2xl bg-indigo-900/30 text-center space-y-3">
                  <Upload className="w-10 h-10 text-lime-400 mx-auto animate-bounce" />
                  <div>
                    <h4 className="text-sm font-bold text-white">Upload `.csv` or `.txt` file</h4>
                    <p className="text-xs text-indigo-300">Format: Team Name, Captain Name, Player 1, Player 2</p>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCsvFileUpload(file);
                    }}
                    className="hidden"
                    id="csv_file_input"
                  />
                  <label
                    htmlFor="csv_file_input"
                    className="inline-block px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl cursor-pointer shadow transition"
                  >
                    Choose CSV File
                  </label>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-xs font-bold text-indigo-200 block uppercase">
                    Paste Comma-Separated or Tab-Separated Lines:
                  </label>
                  <textarea
                    rows={5}
                    value={bulkInputText}
                    onChange={(e) => handleParseBulkText(e.target.value)}
                    placeholder={`Team Name, Captain Name, Player 1, Player 2\nHarlem Kings, Kevin Durant, Steph Curry, Klay Thompson\nBrooklyn Nets, Kyrie Irving, James Harden, Joe Harris`}
                    className="w-full bg-indigo-900/80 border border-white/10 rounded-2xl p-3 text-xs font-mono text-white outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
              )}

              {/* Parsed Preview Table */}
              {bulkParsedTeams.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="font-bold text-lime-400">
                      Parsed Teams Preview ({bulkParsedTeams.filter((t) => t.valid).length} Valid)
                    </span>
                    <span className="text-indigo-300 text-[10px]">Review before importing</span>
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-white/10 rounded-2xl bg-indigo-950/90">
                    <table className="w-full text-left text-xs font-mono">
                      <thead>
                        <tr className="bg-indigo-900 text-indigo-200 text-[10px] uppercase">
                          <th className="p-2">#</th>
                          <th className="p-2">Team Name</th>
                          <th className="p-2">Captain</th>
                          <th className="p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {bulkParsedTeams.map((t, idx) => (
                          <tr key={`bulk_row_${idx}`}>
                            <td className="p-2 text-indigo-400">{idx + 1}</td>
                            <td className="p-2 font-bold text-white">{t.name || '—'}</td>
                            <td className="p-2 text-indigo-200">{t.captainName || '—'}</td>
                            <td className="p-2">
                              {t.valid ? (
                                <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[9px] font-bold rounded-full">
                                  ✓ Ready
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 text-[9px] font-bold rounded-full">
                                  ⚠️ {t.error || 'Invalid'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(null);
                    setBulkInputText('');
                    setBulkParsedTeams([]);
                  }}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-indigo-300 hover:text-white bg-white/5"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={bulkParsedTeams.filter((t) => t.valid).length === 0}
                  onClick={() => handleConfirmBulkRegister(showBulkModal)}
                  className="px-5 py-2.5 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 disabled:opacity-40 text-black font-black uppercase italic rounded-xl text-xs transition shadow-lg shadow-lime-400/20 flex items-center space-x-2"
                >
                  <Check className="w-4 h-4 stroke-[3]" />
                  <span>Confirm Import ({bulkParsedTeams.filter((t) => t.valid).length} Teams)</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* STYLIZED BRANDED PDF BRACKET REPORT MODAL */}
      <AnimatePresence>
        {pdfReportTournament && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-indigo-950 border border-lime-400/40 rounded-3xl p-6 max-w-3xl w-full space-y-6 shadow-2xl relative text-white my-8 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-lime-400 text-black rounded-xl">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black italic uppercase">Official Tournament Bracket PDF Report</h3>
                    <p className="text-xs text-indigo-300">Stylized, print-ready certificate & bracket breakdown</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black uppercase text-xs rounded-xl shadow-lg transition flex items-center space-x-1.5"
                  >
                    <Printer className="w-4 h-4 stroke-[2.5]" />
                    <span>Print / Export PDF</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPdfReportTournament(null)}
                    className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Printable Body */}
              <div id="printable-bracket-report" className="p-6 bg-slate-950 rounded-2xl border border-white/10 space-y-6 text-white font-sans">
                {/* Branding Header Banner */}
                <div className="border-b-2 border-lime-400 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center space-x-2 text-lime-400 font-mono text-xs uppercase tracking-widest font-black">
                      <Trophy className="w-4 h-4" />
                      <span>PLAYGROUND LEAGUE • OFFICIAL TOURNAMENT CERTIFICATE</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-black italic uppercase tracking-tight text-white mt-1">
                      {pdfReportTournament.title}
                    </h1>
                    <p className="text-xs text-indigo-200 mt-1 font-medium">
                      📍 {pdfReportTournament.courtName} ({pdfReportTournament.address}) • Date: {pdfReportTournament.startDate}
                    </p>
                  </div>

                  <div className="text-right font-mono text-xs text-indigo-300">
                    <span className="px-3 py-1 bg-lime-400 text-black font-black uppercase rounded-full inline-block mb-1">
                      {pdfReportTournament.sport.toUpperCase()}
                    </span>
                    <p>Format: {pdfReportTournament.format.replace('_', ' ').toUpperCase()}</p>
                    <p className="text-amber-300 font-bold">Prize Pool: {pdfReportTournament.prizePool || '$500'}</p>
                  </div>
                </div>

                {/* Championship Winner, MVP & Attendance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-r from-amber-950 via-indigo-950 to-amber-950 rounded-2xl border border-amber-400 shadow-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="px-2.5 py-0.5 bg-amber-400 text-black text-[10px] font-black uppercase rounded-full">
                        🥇 OFFICIAL CHAMPION
                      </span>
                      <Crown className="w-5 h-5 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-black italic uppercase text-white">
                      {pdfReportTournament.winningTeamName || pdfReportTournament.registeredTeams[0]?.name || 'Championship Winner'}
                    </h3>
                    <p className="text-xs text-amber-200 font-mono">
                      Captain: {pdfReportTournament.registeredTeams[0]?.captainName || 'Team Captain'}
                    </p>
                  </div>

                  {computeTournamentMVP(pdfReportTournament) && (
                    <div className="p-4 bg-gradient-to-r from-indigo-900 to-slate-900 rounded-2xl border border-lime-400/60 shadow-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[10px] font-black uppercase rounded-full">
                          🎖️ TOURNAMENT MVP
                        </span>
                        <Award className="w-5 h-5 text-lime-400" />
                      </div>
                      {(() => {
                        const mvp = computeTournamentMVP(pdfReportTournament)!;
                        return (
                          <div>
                            <h3 className="text-xl font-black italic uppercase text-white">{mvp.playerName}</h3>
                            <p className="text-xs text-lime-300 font-mono font-bold mt-0.5">
                              {mvp.totalPts} Total PTS • {mvp.totalReb} REB • {mvp.totalAst} AST ({mvp.matchesCount} Matches)
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="p-4 bg-indigo-900/50 rounded-2xl border border-cyan-400/50 shadow-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="px-2.5 py-0.5 bg-cyan-400 text-black text-[10px] font-black uppercase rounded-full">
                        🏟️ ATTENDANCE & REACH
                      </span>
                      <Users className="w-5 h-5 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-black italic uppercase text-white">
                      {pdfReportTournament.estimatedAttendance || 1250} Spectators
                    </h3>
                    <p className="text-xs text-cyan-200 font-mono">
                      Live Gate & Playground Stream Viewers
                    </p>
                  </div>
                </div>

                {/* Top Scoring Teams Summary */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-indigo-200 border-b border-white/10 pb-1 flex items-center space-x-1">
                    <BarChart2 className="w-4 h-4 text-lime-400" />
                    <span>Top-Scoring Team Leaderboard</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {pdfReportTournament.registeredTeams.map((tm, tIdx) => {
                      const radarStats = computeTeamRadarStats(tm, pdfReportTournament);

                      return (
                        <div key={tm.id} className="p-3 bg-indigo-900/40 rounded-xl border border-white/10 flex items-center justify-between font-mono text-xs">
                          <div className="flex items-center space-x-2">
                            <span className="w-6 h-6 rounded-lg bg-lime-400 text-black font-black flex items-center justify-center text-[10px]">
                              #{tIdx + 1}
                            </span>
                            <div>
                              <span className="font-extrabold text-white block">{tm.name}</span>
                              <span className="text-[10px] text-indigo-300">Captain: {tm.captainName}</span>
                            </div>
                          </div>

                          <div className="text-right">
                            <span className="font-black text-lime-300 text-sm">{radarStats.ppg} PPG</span>
                            <span className="text-[10px] text-indigo-300 block">{radarStats.winRate}% Wins</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Match Highlight Clips Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-indigo-200 border-b border-white/10 pb-1 flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                      <Radio className="w-4 h-4 text-rose-400 animate-pulse" />
                      <span>Official Recorded Match Highlight Clips ({pdfReportTournament.highlightClips?.length || 2})</span>
                    </span>
                    <span className="text-[10px] text-indigo-300 font-mono">HD Playground Cam Replays</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(pdfReportTournament.highlightClips && pdfReportTournament.highlightClips.length > 0
                      ? pdfReportTournament.highlightClips
                      : [
                          {
                            id: 'clip_1',
                            title: 'Championship Game Winning Buzzer Beater 🏀',
                            duration: '0:45',
                            thumbnailUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=600&q=80',
                            videoUrl: 'https://playgroundleague.app/clips/championship-buzzer-beater',
                            matchDescription: 'Finals Match #3 • Harlem Kings vs Brooklyn Ballers',
                          },
                          {
                            id: 'clip_2',
                            title: 'Tournament MVP 360 Windmill Dunk Highlight ⚡',
                            duration: '0:30',
                            thumbnailUrl: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&w=600&q=80',
                            videoUrl: 'https://playgroundleague.app/clips/mvp-windmill-dunk',
                            matchDescription: 'Semifinals Match #2 • Harlem Kings vs Rucker Legends',
                          },
                        ]
                    ).map((clip) => (
                      <div
                        key={clip.id}
                        className="p-3 bg-indigo-900/60 rounded-2xl border border-white/10 space-y-2 relative group hover:border-lime-400/50 transition"
                      >
                        <div className="relative h-32 rounded-xl overflow-hidden bg-black">
                          <img
                            src={clip.thumbnailUrl}
                            alt={clip.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300 opacity-80"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

                          <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 text-white rounded font-mono text-[10px] font-bold">
                            {clip.duration}
                          </span>

                          <button
                            type="button"
                            onClick={() => {
                              window.open(clip.videoUrl, '_blank');
                              triggerToast(`Opening match replay clip: ${clip.title}`);
                            }}
                            className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-lime-400/90 text-black flex items-center justify-center shadow-lg group-hover:scale-110 transition"
                          >
                            <Play className="w-5 h-5 fill-current ml-0.5" />
                          </button>
                        </div>

                        <div>
                          <h5 className="font-extrabold text-xs text-white truncate">{clip.title}</h5>
                          <p className="text-[10px] text-indigo-300 font-mono mt-0.5">{clip.matchDescription}</p>
                        </div>

                        <div className="flex items-center justify-between pt-1 text-[10px]">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(clip.videoUrl);
                              triggerToast('Highlight clip link copied! 🎬');
                            }}
                            className="text-lime-300 hover:underline font-mono font-bold flex items-center space-x-1"
                          >
                            <Copy className="w-3 h-3" />
                            <span>Copy Clip Link</span>
                          </button>

                          <span className="text-indigo-400 font-mono text-[9px]">1080p 60FPS</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Matches Table */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-indigo-200 border-b border-white/10 pb-1 flex items-center space-x-1">
                    <Swords className="w-4 h-4 text-lime-400" />
                    <span>Full Bracket Match Results Matrix ({pdfReportTournament.matches.length} Matches)</span>
                  </h4>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs font-mono">
                      <thead>
                        <tr className="bg-indigo-900/80 text-lime-400 border-b border-white/10 uppercase text-[10px]">
                          <th className="p-2.5">Match #</th>
                          <th className="p-2.5">Round Name</th>
                          <th className="p-2.5">Team 1</th>
                          <th className="p-2.5 text-center">Score</th>
                          <th className="p-2.5">Team 2</th>
                          <th className="p-2.5">Winner</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {pdfReportTournament.matches.map((m) => {
                          const isCompleted = m.status === 'completed';
                          const winnerName = m.winnerId === m.team1?.id ? m.team1?.name : m.winnerId === m.team2?.id ? m.team2?.name : m.winnerId || 'TBD';

                          return (
                            <tr key={m.id} className="hover:bg-white/5">
                              <td className="p-2.5 font-bold text-indigo-300">#{m.matchNumber}</td>
                              <td className="p-2.5 font-bold text-white">{m.roundName}</td>
                              <td className={`p-2.5 ${m.winnerId === m.team1?.id ? 'text-lime-400 font-extrabold' : 'text-indigo-200'}`}>
                                {m.team1?.name || 'TBD'}
                              </td>
                              <td className="p-2.5 text-center font-black bg-black/40 text-amber-300 rounded">
                                {m.score1} - {m.score2}
                              </td>
                              <td className={`p-2.5 ${m.winnerId === m.team2?.id ? 'text-lime-400 font-extrabold' : 'text-indigo-200'}`}>
                                {m.team2?.name || 'TBD'}
                              </td>
                              <td className="p-2.5 font-extrabold text-lime-300">
                                {isCompleted ? `🏆 ${winnerName}` : 'Scheduled'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer Bar */}
                <div className="pt-4 border-t-2 border-white/10 flex items-center justify-between text-[10px] font-mono text-indigo-300">
                  <span>Playground League Official Bracket Report ID: {pdfReportTournament.id}</span>
                  <span>Generated: {new Date().toLocaleDateString()} • Playground Analytics</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    const summaryText = `🏆 TOURNAMENT REPORT: ${pdfReportTournament.title}\nVenue: ${pdfReportTournament.courtName}\nWinner: ${pdfReportTournament.winningTeamName || pdfReportTournament.registeredTeams[0]?.name || 'TBD'}\nMatches Played: ${pdfReportTournament.matches.length}\nPrize Pool: ${pdfReportTournament.prizePool}\nPlayground League #BracketReport`;
                    navigator.clipboard.writeText(summaryText);
                    triggerToast('Bracket report summary copied to clipboard! 📋');
                  }}
                  className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl text-xs font-bold transition border border-white/10 flex items-center space-x-1.5"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copy Text Summary</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPdfReportTournament(null)}
                  className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition"
                >
                  Close Report
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SIMULATED TEAM ENTRY FEE PAYMENT MODAL */}
      <AnimatePresence>
        {payingTeamData && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-indigo-950 border border-amber-400/50 rounded-3xl p-6 max-w-md w-full space-y-5 shadow-2xl relative text-white"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2.5 bg-amber-400 text-black rounded-2xl shadow-md">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black italic uppercase text-white">Team Entry Fee Payment</h3>
                    <p className="text-xs text-indigo-300">Secure simulated checkout for Playground League</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setPayingTeamData(null)}
                  className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Summary Card */}
              <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-indigo-300">Tournament:</span>
                  <span className="font-bold text-white truncate max-w-[180px]">{payingTeamData.tournament.title}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-indigo-300">Registering Squad:</span>
                  <span className="font-bold text-lime-400">{payingTeamData.team.name}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-indigo-300">Team Captain:</span>
                  <span className="font-bold text-white">{payingTeamData.team.captainName}</span>
                </div>
                <div className="pt-2 border-t border-white/10 flex items-center justify-between text-sm font-mono font-extrabold">
                  <span className="text-amber-300">Total Due:</span>
                  <span className="text-amber-300 text-base">{payingTeamData.tournament.entryFee || '$20.00'}</span>
                </div>
              </div>

              {/* Payment Method Selector & Inputs */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-indigo-200 block uppercase">
                  Select Payment Method
                </label>
                <div className="grid grid-cols-2 gap-2 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`p-3 rounded-2xl border transition text-left flex items-center space-x-2 ${
                      paymentMethod === 'card'
                        ? 'bg-amber-400/20 border-amber-400 text-amber-300'
                        : 'bg-indigo-900/40 border-white/10 text-indigo-300 hover:text-white'
                    }`}
                  >
                    <CreditCard className="w-4 h-4 shrink-0" />
                    <span>Debit / Credit</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('apple')}
                    className={`p-3 rounded-2xl border transition text-left flex items-center space-x-2 ${
                      paymentMethod === 'apple'
                        ? 'bg-amber-400/20 border-amber-400 text-amber-300'
                        : 'bg-indigo-900/40 border-white/10 text-indigo-300 hover:text-white'
                    }`}
                  >
                    <Coins className="w-4 h-4 shrink-0" />
                    <span>Apple / Google Pay</span>
                  </button>
                </div>

                <div className="space-y-2 text-xs font-mono">
                  <div className="p-3 bg-indigo-900/80 rounded-2xl border border-white/10 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-indigo-300 text-[10px]">Cardholder Name:</span>
                      <span className="font-bold text-white">{payingTeamData.team.captainName}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-lime-300 font-bold">
                      <span>Card Number:</span>
                      <span>•••• •••• •••• 4242</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-indigo-300">
                      <span>Exp: 12/28</span>
                      <span>CVC: 888</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Action Button */}
              <div className="pt-2">
                <button
                  type="button"
                  disabled={isProcessingPayment}
                  onClick={() => handleSimulatedPayment()}
                  className="w-full py-3 bg-gradient-to-r from-amber-400 via-lime-400 to-emerald-400 hover:from-amber-300 hover:to-emerald-300 text-black font-black italic uppercase text-xs rounded-2xl transition shadow-xl shadow-amber-400/20 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {isProcessingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-black" />
                      <span>Processing Payment & Issuing Receipt...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 stroke-[3]" />
                      <span>Confirm & Pay {payingTeamData.tournament.entryFee || '$20.00'}</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* LIVE TOURNAMENT FEED SIDE-DRAWER */}
      <AnimatePresence>
        {isLiveDrawerOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsLiveDrawerOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer Container */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="relative w-full max-w-md bg-indigo-950 border-l border-white/10 p-6 shadow-2xl flex flex-col h-full z-10 text-white space-y-5"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-rose-500/20 text-rose-400 rounded-xl border border-rose-500/30">
                    <Radio className="w-5 h-5 animate-pulse" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-black italic uppercase">Live Tournament Feed</h3>
                      <span className="px-1.5 py-0.5 bg-rose-500 text-white rounded text-[9px] font-black animate-pulse">
                        LIVE TICKER
                      </span>
                    </div>
                    <p className="text-xs text-indigo-300 font-medium">
                      Real-time match updates, MVP awards & team registrations
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsLiveDrawerOpen(false)}
                  className="p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Feed Category Filter Pills */}
              <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 text-xs font-bold">
                {[
                  { id: 'all', label: 'All Updates' },
                  { id: 'results', label: '🏆 Match Scores' },
                  { id: 'mvp', label: '🎖️ MVP Awards' },
                  { id: 'registrations', label: '📝 Registrations' },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setLiveFeedFilter(f.id as any)}
                    className={`px-3 py-1.5 rounded-xl whitespace-nowrap transition ${
                      liveFeedFilter === f.id
                        ? 'bg-rose-500 text-white font-black shadow'
                        : 'bg-indigo-900/60 text-indigo-300 hover:bg-indigo-900'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Scrollable Feed List */}
              <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                {filteredLiveFeed.length > 0 ? (
                  filteredLiveFeed.map((item) => {
                    const reactionCount = feedReactions[item.id] || 0;

                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-indigo-900/40 rounded-2xl border border-white/10 space-y-2 hover:border-lime-400/50 transition relative group"
                      >
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="font-black uppercase tracking-wider text-rose-300 flex items-center space-x-1">
                            {item.type === 'results' && <Trophy className="w-3.5 h-3.5 text-lime-400" />}
                            {item.type === 'mvp' && <Award className="w-3.5 h-3.5 text-amber-400" />}
                            {item.type === 'registrations' && <Users className="w-3.5 h-3.5 text-cyan-400" />}
                            <span>{item.title}</span>
                          </span>
                          <span className="text-indigo-400 font-mono font-bold">{item.timestamp}</span>
                        </div>

                        <p className="text-xs font-semibold text-white leading-snug">
                          {item.description}
                        </p>

                        <div className="flex items-center justify-between pt-2 border-t border-white/5 text-xs">
                          <span className="text-[10px] text-indigo-300/80 italic font-mono">
                            {item.tournamentTitle}
                          </span>

                          <div className="flex items-center space-x-2">
                            {/* Cheer / React Button */}
                            <button
                              type="button"
                              onClick={() => {
                                setFeedReactions((prev) => ({
                                  ...prev,
                                  [item.id]: (prev[item.id] || 0) + 1,
                                }));
                                triggerToast('Cheered for live match update! 👏');
                              }}
                              className="px-2 py-1 bg-white/5 hover:bg-rose-500/20 text-rose-300 rounded-lg text-[11px] font-bold transition flex items-center space-x-1"
                            >
                              <Flame className="w-3 h-3 text-rose-400" />
                              <span>Cheer {reactionCount > 0 && `(${reactionCount})`}</span>
                            </button>

                            {/* Jump to Bracket */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTournamentId(item.tournamentId);
                                setViewTab('current');
                                setIsLiveDrawerOpen(false);
                                triggerToast(`Navigated to ${item.tournamentTitle} bracket!`);
                              }}
                              className="px-2 py-1 bg-lime-400/20 hover:bg-lime-400 text-lime-300 hover:text-black rounded-lg text-[11px] font-bold transition flex items-center space-x-1"
                            >
                              <span>Jump →</span>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="p-8 text-center text-xs text-indigo-300 bg-indigo-900/20 rounded-2xl">
                    No live updates found for this filter.
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="p-3 bg-indigo-900/50 rounded-2xl border border-white/10 text-xs text-indigo-200 text-center font-medium">
                ⚡ Ticker auto-synchronizes live tournament scores and registrations in real-time.
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* REGISTRATION LOCATION SETUP MODAL */}
      <AnimatePresence>
        {showLocationModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-indigo-950 border border-white/10 rounded-3xl p-6 max-w-md w-full text-white space-y-5 shadow-2xl relative"
            >
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                className="absolute top-4 right-4 p-2 text-indigo-300 hover:text-white rounded-xl hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-3">
                <div className="p-3 bg-cyan-400 text-black rounded-2xl">
                  <MapPin className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="text-lg font-black italic uppercase">Athlete Registration Location</h3>
                  <p className="text-xs text-indigo-300">
                    Set your home city and state for localized tournament bracket matching & leaderboard ranking.
                  </p>
                </div>
              </div>

              <StateCitySelector
                selectedState={userRegState}
                selectedCity={userRegCity}
                onStateChange={(newState, defaultCity) => {
                  setUserRegState(newState);
                  setUserRegCity(defaultCity);
                }}
                onCityChange={(newCity) => setUserRegCity(newCity)}
                selectClassName="w-full bg-indigo-900 border border-white/10 rounded-xl px-3 py-2.5 text-sm font-bold text-white outline-none focus:ring-2 focus:ring-cyan-400"
                labelClassName="block text-xs font-black uppercase text-indigo-200 mb-1"
              />

              <div className="flex items-center space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (onUpdateUser) {
                      onUpdateUser({
                        registeredState: userRegState,
                        registeredCity: userRegCity,
                        location: {
                          ...(user?.location || { address: `${userRegCity}, ${userRegState}`, lat: 40.7128, lng: -74.006 }),
                          city: userRegCity,
                          state: userRegState,
                        },
                      });
                    }
                    triggerToast(`Updated registration location to ${userRegCity}, ${userRegState}! 📍`);
                    setShowLocationModal(false);
                  }}
                  className="flex-1 py-3 bg-cyan-400 hover:bg-cyan-300 text-black font-black text-xs uppercase italic rounded-xl shadow-lg shadow-cyan-400/20 transition"
                >
                  Save Registration Location 📍
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification Banner */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-6 right-6 z-50 px-5 py-3 bg-lime-400 text-black rounded-2xl font-black text-xs shadow-2xl flex items-center space-x-2 border border-black/20"
          >
            <Sparkles className="w-4 h-4 text-black shrink-0 animate-spin" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
